/* eslint-disable react/jsx-pascal-case */
import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { CircularProgress } from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";
import earcut from "earcut";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { SpotLight } from "@babylonjs/core/Lights/spotLight";
import { Layer } from "@babylonjs/core/Layers/layer";
import { Texture } from "@babylonjs/core";
import { CubeTexture } from "@babylonjs/core/Materials/Textures/cubeTexture";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { PolygonMeshBuilder } from "@babylonjs/core/Meshes/polygonMesh";
import { Path2 } from "@babylonjs/core/Maths/math.path";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Vector2, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { API_BASE, Palette } from "../../AppConfig";
import { BaseURL, toNumber, degreeToRadians, radiansToDegrees } from "../../components/Common";
import BSL_Button from "../../components/BSL_Button";
import BSL_ColourSelector from "../../components/BSL_ColourSelector";
import BSL_Dialog from "../../components/BSL_Dialog";
import BSL_Select from "../../components/BSL_Select";
import BSL_Slider from "../../components/BSL_Slider";
import BSL_TextInput from "../../components/BSL_TextInput";
import SegmentedButtonGroup from "../../components/SegmentedButtonGroup";
import * as BSL_Common from "../../components/BSL_CommonControls";
import Babylon3DGeneric from "../../components/Babylon3DGeneric";
import { GetBeamHeight } from "./BracketDesignerHelper";

const useStyles = makeStyles((theme) => ({
    root: {},
    label: {
        padding: "0px 7px 0 px 0px",
        margin: "7px 7px",
        font: (theme.brand.LYSAGHT ? "14px " : "14px ") + theme.typography.fontFamily,
        color: Palette.Control.FieldersDarkBlue,
        minWidth: "40px",
        whiteSpace: "nowrap",
    },
}));

let polygon;
var shadowGenerator;

var _scene_data = {
    added_objects: [],
};

const SKYBOX_MODE = 5;

export const valid_brackets = [
    "BK3",
    "BK10",
    "BK22",
    "BK22S",
    "BK22L",
    "BK44",
    "BK46",
    "BK49",
    "BK57",
    "BK77",
    "BK78",
    "CONB",
    "FLUBL",
    "HCCSP",
    "H100C",
    "H150C",
    "H200C",
]; // exclude BKXX for now

export function GetBracketDataForOpal(material_list, job) {
    if (!material_list?.items) return [];
    let formatted_bracket_data = [];
    material_list.items.forEach((item) => {
        if (item?.Category && item.Category.toUpperCase() === "BRACKETS" && item?.QtyCalculated?.Table) {
            let areaIndex = 0;
            if (item?.Area !== undefined) areaIndex = Number(item?.Area) - 1;
            if (areaIndex < 0) areaIndex = 0;

            let colour_default = item?.Colour ? item?.Colour : "Classic Cream";

            item.QtyCalculated.Table.forEach((item_row) => {
                if (item_row?.BracketData) {
                    let row_bracket_data = item_row?.BracketData;
                    if (valid_brackets.includes(row_bracket_data?.BracketType)) {
                        let defaults = GetDefaults(row_bracket_data, areaIndex, colour_default, job);
                        let data = GenerateBracketDataForOpal(
                            row_bracket_data,
                            defaults.BracketType,
                            defaults.AttackAngle,
                            defaults.PitchAngle,
                            defaults.JoinAngle,
                            defaults.RotationAngle,
                            defaults.TopTab,
                            defaults.SideTab,
                            defaults.BeamType,
                            defaults.Gauge,
                            defaults.Colour,
                            item_row?.Qty !== undefined ? item_row?.Qty : 1,
                            row_bracket_data.Use
                                ? row_bracket_data.Use
                                : item_row?.Use !== undefined
                                ? item_row?.Use
                                : ""
                        );
                        formatted_bracket_data.push(data);
                    }
                }
            });
        }
    });

    return formatted_bracket_data;
}

export function GetDefaults(bracketData, areaIndex, colour, job) {
    let default_attackAngle = 90;
    let default_pitchAngle = 0;
    if (bracketData?.Angle1 !== undefined) {
        if (bracketData?.Angle1LeftRight) {
            default_attackAngle = Math.abs(toNumber(bracketData?.Angle1));
            if (bracketData?.LeftRight == "RH") {
                default_attackAngle = 180 - default_attackAngle;
            }
        } else {
            default_pitchAngle = toNumber(bracketData?.Angle1);
        }
    }
    if (bracketData?.Angle2 !== undefined) {
        if (bracketData?.Angle1LeftRight || bracketData?.Angle2LeftRight === false) {
            if (bracketData?.Angle1Header && bracketData?.Angle1Header == "Pitch Angle") {
            } else {
                default_pitchAngle = toNumber(bracketData?.Angle2);
            }
        } else if (bracketData?.Angle2LeftRight !== false) {
            default_attackAngle = Math.abs(toNumber(bracketData?.Angle2));
            if (bracketData?.LeftRight == "RH") {
                default_attackAngle = 180 - default_attackAngle;
            }
        }
    }

    if (default_attackAngle < 10) default_attackAngle = 90;
    if (default_attackAngle > 170) default_attackAngle = 90;

    if (default_pitchAngle) {
        if (bracketData?.UpDown === "Up") {
            default_pitchAngle = -Math.abs(default_pitchAngle);
        } else if (bracketData?.UpDown === "Down") {
            default_pitchAngle = Math.abs(default_pitchAngle);
        }
    }

    let topTab_default = -54;
    if (bracketData?.TopTabStyle) {
        if (bracketData.TopTabStyle === "Upright - Standard") {
            topTab_default = 54;
            if (bracketData.TopTabLength > 0) topTab_default = bracketData.TopTabLength;
        } else if (bracketData.TopTabStyle === "Upright - Long") {
            topTab_default = 108;
            if (bracketData.TopTabLength > 0) topTab_default = bracketData.TopTabLength;
        } else if (bracketData.TopTabStyle.includes("Folded")) topTab_default = -54;
        else if (bracketData.TopTabStyle.includes("None")) topTab_default = 0;
        else topTab_default = bracketData?.TopTabLength ? bracketData?.TopTabLength : 0;
    } else {
        topTab_default = bracketData?.TopTabLength ? bracketData?.TopTabLength : 0;
    }

    let default_BracketType = "Custom";
    if (bracketData?.BracketTypeDesigner) default_BracketType = bracketData?.BracketTypeDesigner;

    let default_JoinAngle = 90;

    if (default_BracketType === "Hip Lower") {
        default_pitchAngle = 0;
        if (job?.AreaSpecifications?.[areaIndex]?.SHAPE.RoofPitch)
            default_pitchAngle = job?.AreaSpecifications?.[areaIndex]?.SHAPE.RoofPitch;
        if (bracketData) {
            default_JoinAngle = toNumber(bracketData?.Angle2);
            if (bracketData.LeftRight === "RH")
                default_attackAngle = Math.round(default_JoinAngle / 2) - toNumber(bracketData?.Angle1);
            else default_attackAngle = toNumber(bracketData?.Angle1) - Math.round(default_JoinAngle / 2);
        }
    }
    if (default_BracketType === "Hip Upper") {
        default_pitchAngle = 0;
        if (job?.AreaSpecifications?.[areaIndex]?.SHAPE.RoofPitch) {
            default_pitchAngle = -job?.AreaSpecifications?.[areaIndex]?.SHAPE.RoofPitch;
            if (bracketData?.Use && bracketData?.Use.includes("to Post")) default_pitchAngle = -default_pitchAngle;
        }
        if (bracketData) {
            default_JoinAngle = toNumber(bracketData?.Angle2);
            if (bracketData.LeftRight === "RH")
                default_attackAngle = Math.round(default_JoinAngle / 2) + toNumber(bracketData?.Angle1);
            else default_attackAngle = -(Math.round(default_JoinAngle / 2) - toNumber(bracketData?.Angle1));
        }
    }

    if (default_BracketType === "Connector") {
        default_JoinAngle = toNumber(bracketData?.Angle1);
    }

    let default_rotationAngle = 0;
    if (default_BracketType === "Rotated") {
        default_pitchAngle = 0;
        default_rotationAngle = toNumber(bracketData?.RotationAngle);
    }

    let default_extend_tabs = window.LYSAGHT ? true : false;
    if (default_BracketType === "Connector") default_extend_tabs = true;

    let default_side_tab_length = 50;
    if ((window.FIELDERS && default_attackAngle === 0) || (default_attackAngle > 80 && default_attackAngle < 100))
        default_side_tab_length = 30;

    if (default_attackAngle !== 0 && (default_attackAngle < 45 || default_attackAngle > 135))
        default_side_tab_length = 60;

    if (bracketData?.SideTabLength >= 30) default_side_tab_length = bracketData.SideTabLength;

    return {
        BracketType: default_BracketType,
        AttackAngle: default_attackAngle,
        PitchAngle: default_pitchAngle,
        JoinAngle: default_JoinAngle,
        RotationAngle: default_rotationAngle,
        TopTab: topTab_default,
        SideTab: default_side_tab_length,
        BeamType: bracketData?.BeamSize ? bracketData.BeamSize : window.LYSAGHT ? "FL15015" : "1586510",
        Gauge: 1.2,
        Colour: colour,
        ExtendTabs: default_extend_tabs,
    };
}

export function GenerateBracketDataForOpal(
    bracketData,
    bracketType,
    attackAngle,
    pitchAngle,
    joinAngle,
    rotationAngle,
    topTab,
    sideTab,
    beamType,
    gauge,
    colour,
    qty = 1,
    use = null,
    autoExtendTabs = null
) {
    let ftt = 0;
    if (topTab === 0) ftt = 3;
    else if (topTab < 0) ftt = 2;
    else if (topTab > 60) ftt = 1;

    let return_data = {
        "Bracket Type": bracketData?.BracketType ? bracketData?.BracketType : bracketType,
        Bracket:
            bracketData?.BracketType === "BK3"
                ? "BK3"
                : bracketData?.BracketType === "BK22"
                ? "BK22"
                : bracketData?.BracketType === "BK22L"
                ? "BK22L"
                : bracketData?.BracketType === "BK22S"
                ? "BK22"
                : bracketData?.BracketType === "BK46"
                ? "BK46"
                : bracketData?.BracketType === "BK57"
                ? "BK57"
                : bracketData?.BracketTypeCent
                ? bracketData?.BracketTypeCent
                : bracketType === "Custom"
                ? "BKXX"
                : bracketType === "Connector"
                ? "BK49"
                : bracketType === "Rotated"
                ? "BK57"
                : bracketType === "Hip Upper"
                ? "BK44"
                : bracketType === "Hip Lower"
                ? "BK10"
                : bracketData?.BracketType
                ? bracketData?.BracketType
                : bracketType,
        Width: window.LYSAGHT ? 50 : 65,
        Depth: GetBeamHeight(beamType),
        "Angle 1":
            bracketType === "Connector"
                ? joinAngle
                : bracketType === "Hip Lower"
                ? joinAngle / 2 + attackAngle
                : bracketType === "Hip Upper"
                ? joinAngle / 2 - attackAngle
                : bracketType === "Rotated"
                ? attackAngle === 90
                    ? 0.01
                    : attackAngle > 90
                    ? -(attackAngle - 90)
                    : 90 - attackAngle
                : bracketData?.Angle1LeftRight
                ? attackAngle > 90
                    ? 180 - attackAngle
                    : -attackAngle
                : pitchAngle,
        "Angle 2":
            bracketType === "Hip Upper" || bracketType === "Hip Lower"
                ? joinAngle
                : bracketData?.Angle1LeftRight || bracketType === "Rotated"
                ? pitchAngle
                : attackAngle === 90
                ? 90
                : attackAngle > 90
                ? 180 - attackAngle
                : -attackAngle,
        "Angle 3": bracketType === "Hip Upper" || bracketType === "Hip Lower" ? -pitchAngle : rotationAngle,
        "Length 1": ftt === 3 ? 0 : ftt === 2 ? sideTab : topTab,
        "Length 2": sideTab,
        ftt: ftt,
        "Bracket Name": "bracket_name",
        "Drawing Name": "drawing_name",
        Material: gauge + "mm GALV P/C",
        "Colour ID": colour,
        Num: qty,
        "BL ID": bracketData?.BL_ID ? bracketData?.BL_ID : 0,
        Use: use ? use : bracketData?.Use ? bracketData?.Use : "",
        "Auto Extend Tabs": autoExtendTabs === null ? (window.LYSAGHT ? 1 : 0) : autoExtendTabs ? 1 : 0,
        DisplayText: bracketData?.DisplayText ? bracketData?.DisplayText : "",
    };

    if (return_data.DisplayText)
        return_data["Bracket Name"] =
            return_data.Bracket + "." + return_data.Depth + return_data.Width + "." + return_data.DisplayText;

    return return_data;
}

export default function BracketDesigner(props) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const job = useSelector((state) => state.job);
    const user_id = useSelector((state) => state?.app?.user?.id);
    const user = useSelector((state) => state?.app?.user);

    const { onClose, mode, bracketDataCollection = {} } = props;

    let bracketData = bracketDataCollection?.BracketData;
    let item = bracketDataCollection?.item;
    let page_setup = bracketDataCollection?.page_setup;

    if (!page_setup) {
        page_setup = {
            Angle1: true,
            Angle1Header: "Pitch Angle",
            UpDown: true,
            Angle2: true,
            Angle2Header: "Join Angle",
            LeftRight: true,
            Editable: false,
        };
    }

    let areaIndex = 0;
    if (item?.Area !== undefined) areaIndex = Number(item?.Area) - 1;
    if (areaIndex < 0) areaIndex = 0;

    let colour_default = item?.Colour ? item?.Colour : "Classic Cream";

    let defaults = GetDefaults(bracketData, areaIndex, colour_default, job);

    const colours = useSelector((state) => state?.config?.colour_arrays?.["Colorbond & Zincalume"]);
    const colour_map = useSelector((state) => state?.config?.colour_map);

    const [viewMode, setViewMode] = React.useState(mode === "View");
    const [limitedEdit, setLimitedEdit] = React.useState(mode === "LimitedEdit");
    const [drawingSVG, setDrawingSVG] = React.useState(null);
    const [show, setShow] = React.useState(true);
    const [flatten, setFlatten] = React.useState(false);
    const [initialise, setInitialise] = React.useState(false);

    const [bracketType, setBracketType] = React.useState(defaults.BracketType);

    const [attackAngle, setAttackAngle] = React.useState(defaults.AttackAngle);
    const [pitchAngle, setPitchAngle] = React.useState(defaults.PitchAngle);
    const [joinAngle, setJoinAngle] = React.useState(defaults.JoinAngle);

    const [beamRotationAngleExtra, setBeamRotationAngleExtra] = React.useState(0);
    const [beamShear, setBeamShear] = React.useState(0);
    const [beamShearX, setBeamShearX] = React.useState(0);

    const [topTab, setTopTab] = React.useState(defaults.TopTab);
    const [sideTab, setSideTab] = React.useState(defaults.SideTab);
    const [beamType, setBeamType] = React.useState(defaults.BeamType);
    const [gauge, setGauge] = React.useState(defaults.Gauge);
    const [colour, setColour] = React.useState(defaults.Colour);
    const [rotationAngle, setRotationAngle] = React.useState(defaults.RotationAngle);
    const [mountingHoles, setMountingHoles] = React.useState(false);
    const [extendTabs, setExtendTabs] = React.useState(defaults.ExtendTabs);

    const [drawingDetails, setDrawingDetails] = React.useState({
        "Job Number": job?.id,
        "Bracket Name": "",
        "Drawing Name": "",
        Use: "",
        Num: 1,
    });

    let perpendicular_pitch_compount_angle = radiansToDegrees(
        Math.atan(Math.tan(degreeToRadians(pitchAngle)) / Math.sin(degreeToRadians(Math.abs(attackAngle))))
    );

    useEffect(() => {
        //
        if (_scene_data.scene) {
            if (_scene_data?.scene?.meshes?.length)
                for (var i = 0; i < _scene_data.scene.meshes.length; i++) {
                    let el = _scene_data.scene.meshes?.[i];
                    if (el) {
                        if (el.name.includes("skyBox")) {
                        } else {
                            el.dispose();
                            i--;
                        }
                    }
                }
            let width = window.LYSAGHT || beamType.startsWith("FL") ? 50 : 65;
            let height = GetBeamHeight(beamType);

            height += 4;
            width += 4;

            if (bracketType === "Hip Lower") {
                addHipLowerBracket(
                    width,
                    height,
                    toNumber(topTab),
                    toNumber(sideTab),
                    mountingHoles,
                    degreeToRadians(joinAngle),
                    degreeToRadians(attackAngle),
                    degreeToRadians(pitchAngle),
                    colour_map?.[colour].Col1,
                    gauge,
                    true
                );
            } else if (bracketType === "Hip Upper") {
                addHipUpperBracket(
                    width,
                    height,
                    toNumber(topTab),
                    toNumber(sideTab),
                    mountingHoles,
                    degreeToRadians(joinAngle),
                    degreeToRadians(attackAngle),
                    colour_map?.[colour].Col1,
                    gauge,
                    true
                );
            } else if (bracketType === "Connector") {
                let bracket = addBracket(
                    width,
                    height,
                    toNumber(0),
                    toNumber(50),
                    mountingHoles,
                    degreeToRadians(0),
                    degreeToRadians((180 - joinAngle) / 2),
                    colour_map?.[colour].Col1,
                    gauge,
                    false
                );

                bracket.rotation.y = degreeToRadians(90);

                let bracket2 = addBracket(
                    width,
                    height,
                    toNumber(0),
                    toNumber(50),
                    mountingHoles,
                    degreeToRadians(0),
                    degreeToRadians(-(180 - joinAngle) / 2),
                    colour_map?.[colour].Col1,
                    gauge,
                    false
                );

                bracket2.rotation.y = degreeToRadians(-90);
            } else if (bracketType === "Rotated") {
                addRotationalBracket(
                    width,
                    height,
                    toNumber(topTab),
                    toNumber(sideTab),
                    mountingHoles,
                    degreeToRadians(rotationAngle),
                    degreeToRadians(90 - attackAngle),
                    colour_map?.[colour].Col1,
                    gauge,
                    true
                );
            } else {
                addBracket(
                    width,
                    height,
                    toNumber(topTab),
                    toNumber(sideTab),
                    mountingHoles,
                    degreeToRadians(pitchAngle),
                    degreeToRadians(90 - attackAngle),
                    colour_map?.[colour].Col1,
                    gauge,
                    true
                );
            }
        }
    }, [
        colour,
        beamType,
        mountingHoles,
        initialise,
        topTab,
        sideTab,
        pitchAngle,
        attackAngle,
        flatten,
        gauge,
        joinAngle,
        bracketType,
        rotationAngle,
        beamShear,
        beamShearX,
        beamRotationAngleExtra,
        extendTabs,
    ]);

    // BABYLON FUNCTIONS
    const addHipLowerBracket = (
        width,
        height,
        topTab,
        sideTab,
        mountingHoles,
        joinAngle,
        attackAngle,
        pitchAngle,
        hex_colour = "#E9DCB8",
        gauge,
        backPlateHoles = true
    ) => {
        if (_scene_data.scene) {
            let scene = _scene_data.scene;

            let bracket_hole_inset = 25;
            let flat = flatten;
            if (height < 80) bracket_hole_inset = 20;
            let bracket_back_top_hole_inset = bracket_hole_inset;
            if (topTab > 30) bracket_back_top_hole_inset = 27;
            //Polygon shape in XoZ plane
            let offsetAngle = attackAngle + joinAngle / 2;

            var bracketMaterial = new StandardMaterial("redMat", scene);
            bracketMaterial.diffuseColor = Color3.FromHexString(hex_colour);
            bracketMaterial.ambientColor = Color3.FromHexString(hex_colour);
            bracketMaterial.backFaceCulling = false;

            var errorMaterial = new StandardMaterial("errorMat", scene);
            errorMaterial.diffuseColor = Color3.FromHexString("#880000");
            errorMaterial.ambientColor = Color3.FromHexString("#880000");
            errorMaterial.backFaceCulling = false;

            // FASCIA PLANES
            let fascia_length = 300;
            const fascia = CreateBox("box", { height: 185, width: 1, depth: fascia_length }, scene);

            fascia.setPivotPoint(new Vector3(0, -185 / 2, -fascia_length / 2));

            fascia.enableEdgesRendering(0.999);
            fascia.edgesColor = new Color4(0.0, 0.0, 0.0, 0.3);
            fascia.edgesWidth = 50;

            var fasciaMeterial = new StandardMaterial("redMat", scene);
            fasciaMeterial.diffuseColor = Color3.FromHexString("#FFFFFF");
            fasciaMeterial.ambientColor = Color3.FromHexString("#FFFFFF");
            fasciaMeterial.backFaceCulling = false;
            fasciaMeterial.alpha = 0.4;
            fascia.material = fasciaMeterial;
            fascia.position.z = fascia_length / 2;
            fascia.position.y = height / 2 - 185 / 2;
            fascia.position.x = -3;

            fascia.rotation.y = Math.PI * 0.5 - offsetAngle;

            const fascia2 = CreateBox("box", { height: 185, width: 1, depth: fascia_length }, scene);

            fascia2.setPivotPoint(new Vector3(0, -185 / 2, -fascia_length / 2));

            fascia2.enableEdgesRendering(0.999);
            fascia2.edgesColor = new Color4(0.0, 0.0, 0.0, 0.3);
            fascia2.edgesWidth = 50;

            fascia2.material = fasciaMeterial;
            fascia2.position.z = fascia_length / 2;
            fascia2.position.y = height / 2 - 185 / 2;
            fascia2.position.x = -3;

            fascia2.rotation.y = -Math.PI * 1.5 + joinAngle - offsetAngle;

            // BASE BRACKET
            let shape = [new Vector2(0, 0), new Vector2(0, 1), new Vector2(1, 1), new Vector2(0, 0)];
            const bracket_tri = new PolygonMeshBuilder("bracket", shape, scene, earcut);
            const bracket = bracket_tri.build(false, gauge); //updatable or not, depth

            // // BOTTOM TAB

            let front_left_void = width / 2 / Math.tan(joinAngle - offsetAngle);
            let front_right_void = width / 2 / Math.tan(offsetAngle);
            let overall_length_1 = 40 + front_right_void;
            let overall_length_2 = 40 + front_left_void;
            let overall_length = Math.max(overall_length_1, overall_length_2);
            let bottom_tab_length = width;

            shape = [
                new Vector2(overall_length, -bottom_tab_length / 2),
                new Vector2(front_left_void, -bottom_tab_length / 2),
                new Vector2(0, 0),
                new Vector2(front_right_void, bottom_tab_length / 2),
                new Vector2(overall_length, bottom_tab_length / 2),
            ];

            const base_tab_tri = new PolygonMeshBuilder("polytri", shape, scene, earcut);
            // base_tab_tri.addHole(mountHole.getPoints());
            const base_tab = base_tab_tri.build(false, gauge); //updatable or not, depth
            base_tab.parent = bracket;
            base_tab.position.y = -(height / 2) + gauge;
            base_tab.material = bracketMaterial;

            base_tab.rotation.z = pitchAngle;

            //LEFT BACK PLATE

            let left_tab_height = height + gauge;
            let left_tab_width = width / 2 / Math.cos(Math.PI / 2 - joinAngle + offsetAngle);

            let factor = Math.tan(pitchAngle) * Math.sin(joinAngle / 2);
            let x = left_tab_width * Math.tan(joinAngle / 2);
            let pitch_adjustment_l1 = x * factor;

            shape = [
                new Vector2(left_tab_height / 2, 0),
                new Vector2(-left_tab_height / 2, 0),
                new Vector2(-left_tab_height / 2 + pitch_adjustment_l1, left_tab_width),
                new Vector2(left_tab_height / 2 + pitch_adjustment_l1, left_tab_width),
            ];
            const left_tab_tri = new PolygonMeshBuilder("polytri", shape, scene, earcut);

            const left_tab = left_tab_tri.build(false, gauge); //updatable or not, depth
            left_tab.parent = bracket;
            left_tab.rotation.y = Math.PI / 2 + joinAngle / 2 - attackAngle;
            left_tab.rotation.z = Math.PI / 2;
            left_tab.material = bracketMaterial;

            // RIGHT BACK PLATE

            let right_tab_height = height + gauge;
            let right_tab_width = width / 2 / Math.sin(offsetAngle);

            x = right_tab_width * Math.tan(joinAngle / 2);
            let pitch_adjustment_r1 = x * factor;

            shape = [
                new Vector2(right_tab_height / 2, 0),
                new Vector2(-right_tab_height / 2, 0),
                new Vector2(-right_tab_height / 2 + pitch_adjustment_r1, right_tab_width),
                new Vector2(right_tab_height / 2 + pitch_adjustment_r1, right_tab_width),
            ];
            const right_tab_tri = new PolygonMeshBuilder("polytri", shape, scene, earcut);

            const right_tab = right_tab_tri.build(false, gauge); //updatable or not, depth
            right_tab.parent = bracket;
            right_tab.rotation.y = Math.PI / 2 + joinAngle / 2 - attackAngle - joinAngle;
            right_tab.rotation.z = Math.PI / 2;
            right_tab.material = bracketMaterial;

            //LEFT TAB PLATE

            left_tab_height = height + gauge;
            left_tab_width = overall_length - front_left_void;
            factor = Math.tan(pitchAngle);
            let pitch_adjustment_l2 = pitch_adjustment_l1 + left_tab_width * factor;

            shape = [
                new Vector2(left_tab_height / 2 + pitch_adjustment_l1, 0),
                new Vector2(-left_tab_height / 2 + pitch_adjustment_l1, 0),
                new Vector2(-left_tab_height / 2 + pitch_adjustment_l2, left_tab_width),
                new Vector2(left_tab_height / 2 + pitch_adjustment_l2, left_tab_width),
            ];
            let left_tab_plate_tri = new PolygonMeshBuilder("polytri", shape, scene, earcut);

            const left_tab_front = left_tab_plate_tri.build(false, gauge); //updatable or not, depth
            left_tab_front.parent = bracket;
            left_tab_front.position.x = front_left_void;
            left_tab_front.position.z = -bottom_tab_length / 2;
            left_tab_front.rotation.y = Math.PI / 2;
            left_tab_front.rotation.z = Math.PI / 2;
            left_tab_front.material = bracketMaterial;

            //right TAB PLATE

            right_tab_height = height + gauge;
            right_tab_width = overall_length - front_right_void;
            let pitch_adjustment_r2 = pitch_adjustment_r1 + right_tab_width * factor;

            shape = [
                new Vector2(right_tab_height / 2 + pitch_adjustment_r1, 0),
                new Vector2(-right_tab_height / 2 + pitch_adjustment_r1, 0),
                new Vector2(-right_tab_height / 2 + pitch_adjustment_r2, right_tab_width),
                new Vector2(right_tab_height / 2 + pitch_adjustment_r2, right_tab_width),
            ];
            let right_tab_plate_tri = new PolygonMeshBuilder("polytri", shape, scene, earcut);

            const right_tab_front = right_tab_plate_tri.build(false, gauge); //updatable or not, depth
            right_tab_front.parent = bracket;
            right_tab_front.position.x = front_right_void;
            right_tab_front.position.z = bottom_tab_length / 2;
            right_tab_front.rotation.y = Math.PI / 2;
            right_tab_front.rotation.z = Math.PI / 2;
            right_tab_front.material = bracketMaterial;

            //BEAM

            let beam_length = 300;
            const beam = CreateBox("box", { height: height - 4, width: width - 4, depth: beam_length }, scene);
            beam.parent = bracket;
            beam.setPivotPoint(new Vector3(0, -height / 2, beam_length / 2));
            // if (pitchAngle > 0) {
            //     beam.setPivotPoint(new Vector3(0, -height / 2, beam_length / 2));
            // }
            beam.position.z = -beam_length / 2;
            beam.rotation.y = -Math.PI / 2;
            // beam.rotation.x = pitchAngle;
            beam.position.x = gauge + overall_length - 38;
            beam.position.y = gauge + 1;
            beam.position.x -= x * 0.75;
            beam.rotation.x = pitchAngle;

            // if (pitchAngle < 0) {
            //     beam.position.y = beam.position.y + height / Math.cos(pitchAngle) - height * Math.cos(pitchAngle); // * Math.tan(degreeToRadians(pitchAngle));
            // }
            // if (pitchAngle > 0) {
            //     beam.position.y = beam.position.y - 38 * Math.tan(pitchAngle);
            // }

            beam.enableEdgesRendering(0.999);
            beam.edgesColor = new Color4(0.0, 0.0, 0.0, 0.3);
            beam.edgesWidth = 50;

            var beamMeterial = new StandardMaterial("redMat", scene);
            beamMeterial.diffuseColor = Color3.FromHexString(hex_colour);
            beamMeterial.ambientColor = Color3.FromHexString(hex_colour);
            beamMeterial.backFaceCulling = false;
            beamMeterial.alpha = 0.1;
            beam.material = beamMeterial;
        }
    };

    const addHipUpperBracket = (
        width,
        height,
        topTab,
        sideTab,
        mountingHoles,
        joinAngle,
        attackAngle,
        hex_colour = "#E9DCB8",
        gauge,
        backPlateHoles = true
    ) => {
        if (_scene_data.scene) {
            let scene = _scene_data.scene;

            let bracket_hole_inset = 25;
            let flat = flatten;
            if (height < 80) bracket_hole_inset = 20;
            let bracket_back_top_hole_inset = bracket_hole_inset;
            if (topTab > 30) bracket_back_top_hole_inset = 27;
            //Polygon shape in XoZ plane
            let offsetAngle = attackAngle + joinAngle / 2;

            var bracketMaterial = new StandardMaterial("redMat", scene);
            bracketMaterial.diffuseColor = Color3.FromHexString(hex_colour);
            bracketMaterial.ambientColor = Color3.FromHexString(hex_colour);
            bracketMaterial.backFaceCulling = false;

            var errorMaterial = new StandardMaterial("errorMat", scene);
            errorMaterial.diffuseColor = Color3.FromHexString("#880000");
            errorMaterial.ambientColor = Color3.FromHexString("#880000");
            errorMaterial.backFaceCulling = false;

            // FASCIA PLANES
            let fascia_length = 300;
            const fascia = CreateBox("box", { height: 185, width: 1, depth: fascia_length }, scene);

            fascia.setPivotPoint(new Vector3(0, -185 / 2, -fascia_length / 2));

            fascia.enableEdgesRendering(0.999);
            fascia.edgesColor = new Color4(0.0, 0.0, 0.0, 0.3);
            fascia.edgesWidth = 50;

            var fasciaMeterial = new StandardMaterial("redMat", scene);
            fasciaMeterial.diffuseColor = Color3.FromHexString("#FFFFFF");
            fasciaMeterial.ambientColor = Color3.FromHexString("#FFFFFF");
            fasciaMeterial.backFaceCulling = false;
            fasciaMeterial.alpha = 0.6;
            fascia.material = fasciaMeterial;
            fascia.position.z = fascia_length / 2;
            fascia.position.y = -height / 2 + 185 / 2;
            fascia.position.x = -3;

            fascia.rotation.y = Math.PI * 1.5 - offsetAngle;

            const fascia2 = CreateBox("box", { height: 185, width: 1, depth: fascia_length }, scene);

            fascia2.setPivotPoint(new Vector3(0, -185 / 2, -fascia_length / 2));

            fascia2.enableEdgesRendering(0.999);
            fascia2.edgesColor = new Color4(0.0, 0.0, 0.0, 0.3);
            fascia2.edgesWidth = 50;

            fascia2.material = fasciaMeterial;
            fascia2.position.z = fascia_length / 2;
            fascia2.position.y = -height / 2 + 185 / 2;
            fascia2.position.x = -3;

            fascia2.rotation.y = -Math.PI * 0.5 + joinAngle - offsetAngle;

            // BASE BRACKET
            let shape = [new Vector2(0, 0), new Vector2(0, 1), new Vector2(1, 1), new Vector2(0, 0)];
            const bracket_tri = new PolygonMeshBuilder("bracket", shape, scene, earcut);
            const bracket = bracket_tri.build(false, gauge); //updatable or not, depth

            // // BOTTOM TAB

            let base_tab_left_width = 35 + width / 2 / Math.tan(offsetAngle);
            if (base_tab_left_width > 500 || base_tab_left_width < 0) base_tab_left_width = 500;
            let base_tab_right_width = 35 + width / 2 / Math.tan(joinAngle - offsetAngle);
            if (base_tab_right_width > 500 || base_tab_right_width < 0) base_tab_right_width = 500;
            let bottom_tab_length = width;

            let front_base = Math.max(base_tab_left_width, base_tab_right_width);

            shape = [
                new Vector2(front_base, -bottom_tab_length / 2),
                new Vector2(front_base - base_tab_left_width, -bottom_tab_length / 2),
                new Vector2(front_base - 35, 0),
                new Vector2(front_base - base_tab_right_width, bottom_tab_length / 2),
                new Vector2(front_base, bottom_tab_length / 2),
            ];

            const base_tab_tri = new PolygonMeshBuilder("polytri", shape, scene, earcut);
            const base_tab = base_tab_tri.build(false, gauge); //updatable or not, depth
            base_tab.parent = bracket;
            base_tab.position.y = -(height / 2) + gauge;
            base_tab.material = bracketMaterial;

            // LEFT TAB
            let left_tab_height = height + gauge;
            let left_tab_width = base_tab_left_width;

            shape = [
                new Vector2(left_tab_height / 2, 0),
                new Vector2(-left_tab_height / 2, 0),
                new Vector2(-left_tab_height / 2, left_tab_width),
                new Vector2(left_tab_height / 2, left_tab_width),
            ];
            const left_tab_tri = new PolygonMeshBuilder("polytri", shape, scene, earcut);

            let tek_holeSize = 6;
            let holePosX = -left_tab_height / 2 + bracket_back_top_hole_inset;
            let holePosY = left_tab_width - 15;
            let mountHole = new Path2(holePosX - tek_holeSize / 2, holePosY);
            mountHole.addArcTo(holePosX, holePosY + tek_holeSize / 2, holePosX + tek_holeSize / 2, holePosY, 20);
            mountHole.addArcTo(holePosX, holePosY - tek_holeSize / 2, holePosX - tek_holeSize / 2, holePosY, 20);
            if (backPlateHoles) left_tab_tri.addHole(mountHole.getPoints());

            holePosX = left_tab_height / 2 + -bracket_back_top_hole_inset;
            holePosY = left_tab_width - 15;
            mountHole = new Path2(holePosX - tek_holeSize / 2, holePosY);
            mountHole.addArcTo(holePosX, holePosY + tek_holeSize / 2, holePosX + tek_holeSize / 2, holePosY, 20);
            mountHole.addArcTo(holePosX, holePosY - tek_holeSize / 2, holePosX - tek_holeSize / 2, holePosY, 20);
            if (backPlateHoles) left_tab_tri.addHole(mountHole.getPoints());

            const left_tab = left_tab_tri.build(false, gauge); //updatable or not, depth
            left_tab.parent = bracket;
            left_tab.position.x = front_base - left_tab_width;
            left_tab.position.z = -bottom_tab_length / 2;
            left_tab.rotation.y = Math.PI / 2;
            left_tab.rotation.z = Math.PI / 2;
            if (flat) {
                left_tab.rotation.z = Math.PI;
                left_tab.position.y = -left_tab_height / 2;
                left_tab.position.z = -(left_tab_height + bottom_tab_length) / 2;
            }

            if (base_tab_left_width > 300) {
                left_tab.material = errorMaterial;
            } else {
                left_tab.material = bracketMaterial;
            }

            // LEFT FASCIA TAB
            let left_fascia_tab_width = 30;

            shape = [
                new Vector2(left_tab_height / 2, 0),
                new Vector2(-left_tab_height / 2, 0),
                new Vector2(-left_tab_height / 2, left_fascia_tab_width),
                new Vector2(left_tab_height / 2, left_fascia_tab_width),
            ];
            const left_fascia_tab_tri = new PolygonMeshBuilder("polytri", shape, scene, earcut);

            tek_holeSize = 6;
            holePosX = -left_tab_height / 2 + bracket_back_top_hole_inset;
            holePosY = 15;
            mountHole = new Path2(holePosX - tek_holeSize / 2, holePosY);
            mountHole.addArcTo(holePosX, holePosY + tek_holeSize / 2, holePosX + tek_holeSize / 2, holePosY, 20);
            mountHole.addArcTo(holePosX, holePosY - tek_holeSize / 2, holePosX - tek_holeSize / 2, holePosY, 20);
            if (backPlateHoles) left_fascia_tab_tri.addHole(mountHole.getPoints());

            holePosX = left_tab_height / 2 + -bracket_back_top_hole_inset;
            holePosY = 15;
            mountHole = new Path2(holePosX - tek_holeSize / 2, holePosY);
            mountHole.addArcTo(holePosX, holePosY + tek_holeSize / 2, holePosX + tek_holeSize / 2, holePosY, 20);
            mountHole.addArcTo(holePosX, holePosY - tek_holeSize / 2, holePosX - tek_holeSize / 2, holePosY, 20);
            if (backPlateHoles) left_fascia_tab_tri.addHole(mountHole.getPoints());

            const left_fascia_tab = left_fascia_tab_tri.build(false, gauge); //updatable or not, depth
            left_fascia_tab.parent = bracket;
            left_fascia_tab.position.x = front_base - left_tab_width;
            left_fascia_tab.position.z = -bottom_tab_length / 2;
            left_fascia_tab.rotation.y = Math.PI * 1.5 - offsetAngle;
            left_fascia_tab.rotation.z = Math.PI / 2;
            if (flat) {
                left_fascia_tab.rotation.z = Math.PI;
                left_fascia_tab.position.y = -left_tab_height / 2;
                left_fascia_tab.position.z = -(left_tab_height + bottom_tab_length) / 2;
            }

            if (base_tab_left_width > 300) {
                left_fascia_tab.material = errorMaterial;
            } else {
                left_fascia_tab.material = bracketMaterial;
            }

            // RIGHT TAB
            let right_tab_height = height + gauge;
            let right_tab_width = base_tab_right_width;

            shape = [
                new Vector2(right_tab_height / 2, 0),
                new Vector2(-right_tab_height / 2, 0),
                new Vector2(-right_tab_height / 2, right_tab_width),
                new Vector2(right_tab_height / 2, right_tab_width),
            ];
            const right_tab_tri = new PolygonMeshBuilder("polytri", shape, scene, earcut);

            tek_holeSize = 6;
            holePosX = -right_tab_height / 2 + bracket_back_top_hole_inset;
            holePosY = right_tab_width - 15;
            mountHole = new Path2(holePosX - tek_holeSize / 2, holePosY);
            mountHole.addArcTo(holePosX, holePosY + tek_holeSize / 2, holePosX + tek_holeSize / 2, holePosY, 20);
            mountHole.addArcTo(holePosX, holePosY - tek_holeSize / 2, holePosX - tek_holeSize / 2, holePosY, 20);
            if (backPlateHoles) right_tab_tri.addHole(mountHole.getPoints());

            holePosX = right_tab_height / 2 + -bracket_back_top_hole_inset;
            holePosY = right_tab_width - 15;
            mountHole = new Path2(holePosX - tek_holeSize / 2, holePosY);
            mountHole.addArcTo(holePosX, holePosY + tek_holeSize / 2, holePosX + tek_holeSize / 2, holePosY, 20);
            mountHole.addArcTo(holePosX, holePosY - tek_holeSize / 2, holePosX - tek_holeSize / 2, holePosY, 20);
            if (backPlateHoles) right_tab_tri.addHole(mountHole.getPoints());

            const right_tab = right_tab_tri.build(false, gauge); //updatable or not, depth
            right_tab.parent = bracket;
            right_tab.position.x = front_base - right_tab_width;
            right_tab.position.z = bottom_tab_length / 2;
            right_tab.rotation.y = Math.PI / 2;
            right_tab.rotation.z = Math.PI / 2;
            if (flat) {
                left_tab.rotation.z = Math.PI;
                left_tab.position.y = -right_tab_height / 2;
                left_tab.position.z = -(right_tab_height + bottom_tab_length) / 2;
            }

            if (base_tab_right_width > 300) {
                right_tab.material = errorMaterial;
            } else {
                right_tab.material = bracketMaterial;
            }

            // RIGHT FASCIA TAB
            let right_fascia_tab_width = 30;

            shape = [
                new Vector2(right_tab_height / 2, 0),
                new Vector2(-right_tab_height / 2, 0),
                new Vector2(-right_tab_height / 2, right_fascia_tab_width),
                new Vector2(right_tab_height / 2, right_fascia_tab_width),
            ];
            const right_fascia_tab_tri = new PolygonMeshBuilder("polytri", shape, scene, earcut);

            tek_holeSize = 6;
            holePosX = -right_tab_height / 2 + bracket_back_top_hole_inset;
            holePosY = 15;
            mountHole = new Path2(holePosX - tek_holeSize / 2, holePosY);
            mountHole.addArcTo(holePosX, holePosY + tek_holeSize / 2, holePosX + tek_holeSize / 2, holePosY, 20);
            mountHole.addArcTo(holePosX, holePosY - tek_holeSize / 2, holePosX - tek_holeSize / 2, holePosY, 20);
            if (backPlateHoles) right_fascia_tab_tri.addHole(mountHole.getPoints());

            holePosX = right_tab_height / 2 + -bracket_back_top_hole_inset;
            holePosY = 15;
            mountHole = new Path2(holePosX - tek_holeSize / 2, holePosY);
            mountHole.addArcTo(holePosX, holePosY + tek_holeSize / 2, holePosX + tek_holeSize / 2, holePosY, 20);
            mountHole.addArcTo(holePosX, holePosY - tek_holeSize / 2, holePosX - tek_holeSize / 2, holePosY, 20);
            if (backPlateHoles) right_fascia_tab_tri.addHole(mountHole.getPoints());

            const right_fascia_tab = right_fascia_tab_tri.build(false, gauge); //updatable or not, depth
            right_fascia_tab.parent = bracket;
            right_fascia_tab.position.x = front_base - right_tab_width;
            right_fascia_tab.position.z = bottom_tab_length / 2;
            right_fascia_tab.rotation.y = -Math.PI * 0.5 + joinAngle - offsetAngle;
            right_fascia_tab.rotation.z = Math.PI / 2;
            if (flat) {
                right_fascia_tab.rotation.z = Math.PI;
                right_fascia_tab.position.y = -right_tab_height / 2;
                right_fascia_tab.position.z = -(right_tab_height + bottom_tab_length) / 2;
            }

            if (base_tab_right_width > 300) {
                right_fascia_tab.material = errorMaterial;
            } else {
                right_fascia_tab.material = bracketMaterial;
            }

            bracket.position.x = bracket.position.x - front_base + 35;

            let beam_length = 300;
            const beam = CreateBox("box", { height: height, width: width - 2, depth: beam_length }, scene);
            beam.parent = bracket;
            beam.setPivotPoint(new Vector3(0, height / 2, beam_length / 2));
            if (pitchAngle > 0) {
                beam.setPivotPoint(new Vector3(0, -height / 2, beam_length / 2));
            }
            beam.position.z = -beam_length / 2;
            beam.rotation.y = -Math.PI / 2;
            // beam.rotation.x = pitchAngle;
            beam.position.x = gauge + front_base - 38;
            beam.position.y = gauge + 3;
            beam.rotation.x = degreeToRadians(pitchAngle);

            if (pitchAngle < 0) {
                beam.position.y =
                    beam.position.y +
                    height / Math.cos(degreeToRadians(pitchAngle)) -
                    height * Math.cos(degreeToRadians(pitchAngle)); // * Math.tan(degreeToRadians(pitchAngle));
            }
            if (pitchAngle > 0) {
                beam.position.y = beam.position.y - 38 * Math.tan(degreeToRadians(pitchAngle));
            }

            beam.enableEdgesRendering(0.999);
            beam.edgesColor = new Color4(0.0, 0.0, 0.0, 0.3);
            beam.edgesWidth = 50;

            var beamMeterial = new StandardMaterial("redMat", scene);
            beamMeterial.diffuseColor = Color3.FromHexString(hex_colour);
            beamMeterial.ambientColor = Color3.FromHexString(hex_colour);
            beamMeterial.backFaceCulling = false;
            beamMeterial.alpha = 0.1;
            beam.material = beamMeterial;

            if (_scene_data?.scene?.meshes?.length && flat)
                for (var i = 0; i < _scene_data.scene.meshes.length; i++) {
                    let el = _scene_data.scene.meshes?.[i];
                    if (el) {
                        if (el.name.includes("skyBox")) {
                        } else {
                            el.enableEdgesRendering(0.999);
                            el.edgesColor = new Color4(0.0, 0.0, 0.0, 1.0);
                            el.edgesWidth = 100;
                        }
                    }
                }

            return bracket;
        }
    };
    const addBracket = (
        width,
        height,
        topTab,
        sideTab,
        mountingHoles,
        pitchAngle,
        attackAngle,
        hex_colour = "#E9DCB8",
        gauge,
        backPlateHoles = true
    ) => {
        if (_scene_data.scene) {
            let scene = _scene_data.scene;

            let bracket_hole_inset = 25;
            let flat = flatten;
            if (height < 80) bracket_hole_inset = 20;
            let bracket_back_top_hole_inset = bracket_hole_inset;
            if (topTab > 30) bracket_back_top_hole_inset = 27;
            //Polygon shape in XoZ plane

            // FASCIA PLANES
            if (bracketType !== "Connector") {
                let fascia_length = 300;
                const fascia = CreateBox("fascia", { height: 185, width: 1, depth: fascia_length }, scene);

                fascia.setPivotPoint(new Vector3(0, -185 / 2, 0));

                fascia.enableEdgesRendering(0.999);
                fascia.edgesColor = new Color4(0.0, 0.0, 0.0, 0.3);
                fascia.edgesWidth = 50;

                var fasciaMaterial = new StandardMaterial("redMat", scene);
                fasciaMaterial.diffuseColor = Color3.FromHexString("#FFFFFF");
                fasciaMaterial.ambientColor = Color3.FromHexString("#FFFFFF");
                fasciaMaterial.backFaceCulling = false;
                fasciaMaterial.alpha = 0.25;
                fascia.material = fasciaMaterial;

                let top_adjust = 0;
                if (topTab > 0) top_adjust = -topTab;
                fascia.position.y = height / 2 - 80 - top_adjust;
                fascia.position.x = -1;
            }

            var bracketMaterial = new StandardMaterial("redMat", scene);
            bracketMaterial.diffuseColor = Color3.FromHexString(hex_colour);
            bracketMaterial.ambientColor = Color3.FromHexString(hex_colour);
            bracketMaterial.backFaceCulling = false;

            let shape = [new Vector2(0, 0), new Vector2(0, 1), new Vector2(1, 1), new Vector2(0, 0)];
            const bracket_tri = new PolygonMeshBuilder("bracket", shape, scene, earcut);
            const bracket = bracket_tri.build(false, gauge); //updatable or not, depth

            // BACK PLATE

            let back_width = width / Math.cos(attackAngle);
            let back_left_compound_adjustment =
                attackAngle > 0 ? back_width * Math.tan(pitchAngle) * Math.sin(attackAngle) : 0;
            let back_right_compound_adjustment =
                attackAngle < 0 ? back_width * Math.tan(pitchAngle) * -Math.sin(attackAngle) : 0;

            let back_left_tab_height_adj =
                topTab < 0
                    ? height / Math.cos(pitchAngle) - height + back_left_compound_adjustment
                    : -height * Math.cos(pitchAngle) +
                      height +
                      (back_left_compound_adjustment > topTab ? back_left_compound_adjustment : 0);
            let back_right_tab_height_adj =
                topTab < 0
                    ? height / Math.cos(pitchAngle) - height + back_right_compound_adjustment
                    : -height * Math.cos(pitchAngle) +
                      height +
                      (back_right_compound_adjustment > topTab ? back_right_compound_adjustment : 0);

            let topFoldedTab = false;
            if (topTab < 0) {
                topTab = 0;
                topFoldedTab = true;
            }

            radiansToDegrees(pitchAngle);

            let back_height = height + gauge * 2;

            shape = [
                new Vector2(back_height / 2 + topTab + back_left_tab_height_adj, -back_width / 2),
                new Vector2(-back_height / 2 + back_left_compound_adjustment, -back_width / 2),
                new Vector2(-back_height / 2 + back_right_compound_adjustment, back_width / 2),
                new Vector2(back_height / 2 + topTab + back_right_tab_height_adj, back_width / 2),
            ];

            const back_plate_tri = new PolygonMeshBuilder("polytri", shape, scene, earcut);

            let bolt_holeSize = 13;
            let tek_holeSize = 6;
            let holePosX = height / 2 + topTab - bracket_back_top_hole_inset;
            let holePosY = 0;
            let mountHole = new Path2(holePosX - bolt_holeSize / 2, holePosY);
            mountHole.addArcTo(holePosX, holePosY + bolt_holeSize / 2, holePosX + bolt_holeSize / 2, holePosY, 20);
            mountHole.addArcTo(holePosX, holePosY - bolt_holeSize / 2, holePosX - bolt_holeSize / 2, holePosY, 20);
            if (mountingHoles && backPlateHoles) back_plate_tri.addHole(mountHole.getPoints());

            holePosX =
                -height / 2 +
                bracket_hole_inset +
                Math.max(back_left_compound_adjustment, back_right_compound_adjustment);
            holePosY = 0;
            mountHole = new Path2(holePosX - bolt_holeSize / 2, holePosY);
            mountHole.addArcTo(holePosX, holePosY + bolt_holeSize / 2, holePosX + bolt_holeSize / 2, holePosY, 20);
            mountHole.addArcTo(holePosX, holePosY - bolt_holeSize / 2, holePosX - bolt_holeSize / 2, holePosY, 20);
            if (mountingHoles && backPlateHoles) back_plate_tri.addHole(mountHole.getPoints());

            holePosX =
                height / 2 +
                topTab -
                bracket_back_top_hole_inset +
                Math.min(back_left_tab_height_adj, back_right_tab_height_adj);
            holePosY = -(back_width / 2) + 15;
            mountHole = new Path2(holePosX - tek_holeSize / 2, holePosY);
            mountHole.addArcTo(holePosX, holePosY + tek_holeSize / 2, holePosX + tek_holeSize / 2, holePosY, 20);
            mountHole.addArcTo(holePosX, holePosY - tek_holeSize / 2, holePosX - tek_holeSize / 2, holePosY, 20);
            if (backPlateHoles) back_plate_tri.addHole(mountHole.getPoints());

            holePosX =
                height / 2 +
                topTab -
                bracket_back_top_hole_inset +
                Math.min(back_left_tab_height_adj, back_right_tab_height_adj);
            holePosY = back_width / 2 - 15;
            mountHole = new Path2(holePosX - tek_holeSize / 2, holePosY);
            mountHole.addArcTo(holePosX, holePosY + tek_holeSize / 2, holePosX + tek_holeSize / 2, holePosY, 20);
            mountHole.addArcTo(holePosX, holePosY - tek_holeSize / 2, holePosX - tek_holeSize / 2, holePosY, 20);
            if (backPlateHoles) back_plate_tri.addHole(mountHole.getPoints());

            holePosX =
                -height / 2 +
                bracket_hole_inset +
                Math.max(back_left_compound_adjustment, back_right_compound_adjustment);
            holePosY = -(back_width / 2) + 15;
            mountHole = new Path2(holePosX - tek_holeSize / 2, holePosY);
            mountHole.addArcTo(holePosX, holePosY + tek_holeSize / 2, holePosX + tek_holeSize / 2, holePosY, 20);
            mountHole.addArcTo(holePosX, holePosY - tek_holeSize / 2, holePosX - tek_holeSize / 2, holePosY, 20);
            if (backPlateHoles) back_plate_tri.addHole(mountHole.getPoints());

            holePosX =
                -height / 2 +
                bracket_hole_inset +
                Math.max(back_left_compound_adjustment, back_right_compound_adjustment);
            holePosY = back_width / 2 - 15;
            mountHole = new Path2(holePosX - tek_holeSize / 2, holePosY);
            mountHole.addArcTo(holePosX, holePosY + tek_holeSize / 2, holePosX + tek_holeSize / 2, holePosY, 20);
            mountHole.addArcTo(holePosX, holePosY - tek_holeSize / 2, holePosX - tek_holeSize / 2, holePosY, 20);
            if (backPlateHoles) back_plate_tri.addHole(mountHole.getPoints());

            const back_plate = back_plate_tri.build(false, gauge); //updatable or not, depth
            back_plate.parent = bracket;
            back_plate.rotation.z = Math.PI / 2;
            back_plate.material = bracketMaterial;

            // BOTTOM TAB

            let gear = Math.sin(Math.abs(pitchAngle)) * Math.sin(Math.abs(attackAngle));

            let attack_bottom_tab_length_adjustment_factor = Math.cos(attackAngle);
            let pitch_bottom_tab_length_adjustment_factor = 1 / Math.cos(pitchAngle);

            let bottom_pitch_extend_adjustment = 0;
            if (pitchAngle < 0 && extendTabs) {
                bottom_pitch_extend_adjustment = height * Math.sin(-pitchAngle);
            }

            let bottom_tab_width =
                sideTab * pitch_bottom_tab_length_adjustment_factor * attack_bottom_tab_length_adjustment_factor +
                bottom_pitch_extend_adjustment;
            let bottom_tab_length = back_width + gear * 0.2 * back_width;
            let base_tab_left_attack_adjustment = (bottom_tab_width * Math.tan(attackAngle)) / Math.cos(pitchAngle);
            let base_tab_right_attack_adjustment = (bottom_tab_width * Math.tan(attackAngle)) / Math.cos(pitchAngle);

            let base_tab_left_width = bottom_tab_width;
            let base_tab_right_width = bottom_tab_width;

            //calculate side tabs too
            let left_tab_width = sideTab;
            if (attackAngle > 0 && extendTabs) {
                left_tab_width = Math.max(Math.sin(Math.abs(attackAngle)) * bottom_tab_length + sideTab, sideTab);
                base_tab_left_attack_adjustment =
                    ((left_tab_width + bottom_pitch_extend_adjustment) * Math.sin(attackAngle)) / Math.cos(pitchAngle);
                base_tab_left_width = (left_tab_width + bottom_pitch_extend_adjustment) * Math.cos(attackAngle);
            }

            let right_tab_width = sideTab;
            if (attackAngle < 0 && extendTabs) {
                right_tab_width = Math.max(Math.sin(Math.abs(attackAngle)) * bottom_tab_length + sideTab, sideTab);
                base_tab_right_attack_adjustment =
                    ((right_tab_width + bottom_pitch_extend_adjustment) * Math.sin(attackAngle)) / Math.cos(pitchAngle);
                base_tab_right_width = (right_tab_width + bottom_pitch_extend_adjustment) * Math.cos(attackAngle);
            }

            shape = [
                new Vector2(base_tab_left_width, -bottom_tab_length / 2 + base_tab_left_attack_adjustment),
                new Vector2(0, -bottom_tab_length / 2),
                new Vector2(0, bottom_tab_length / 2),
                new Vector2(base_tab_right_width, bottom_tab_length / 2 + base_tab_right_attack_adjustment),
            ];

            const base_tab_tri = new PolygonMeshBuilder("polytri", shape, scene, earcut);
            const base_tab = base_tab_tri.build(false, gauge); //updatable or not, depth
            base_tab.parent = bracket;
            base_tab.position.y =
                -(height / 2) -
                0 +
                (Math.max(Math.abs(back_left_compound_adjustment), Math.abs(back_right_compound_adjustment)) / 2) *
                    (pitchAngle > 0 ? 1 : -1);
            if (!flat) base_tab.rotation.z = -pitchAngle * Math.cos(Math.abs(attackAngle)); // * (attackAngle && pitchAngle ? Math.sin(Math.abs(attackAngle)) * 1.05 : 1);

            let adj_down = 0.35 * Math.sin(Math.abs(attackAngle) * 2.5);

            base_tab.rotation.x = attackAngle * Math.cos(attackAngle) * Math.tan(pitchAngle) * (1.65 - adj_down); // * (attackAngle && pitchAngle ? -Math.sin(attackAngle) * 1.05 : 1);
            if (flat) base_tab.rotation.z = -degreeToRadians(90);

            base_tab.material = bracketMaterial;

            let top_pitch_extend_adjustment = 0;
            if (pitchAngle > 0 && extendTabs) {
                top_pitch_extend_adjustment = height * Math.sin(pitchAngle);
            }

            // TOP TAB
            if (topFoldedTab) {
                let top_tab_width =
                    sideTab * pitch_bottom_tab_length_adjustment_factor * attack_bottom_tab_length_adjustment_factor +
                    top_pitch_extend_adjustment;

                let top_tab_left_width = top_tab_width;
                let top_tab_right_width = top_tab_width;

                let top_tab_left_attack_adjustment = (top_tab_width * Math.tan(attackAngle)) / Math.cos(pitchAngle);
                let top_tab_right_attack_adjustment = (top_tab_width * Math.tan(attackAngle)) / Math.cos(pitchAngle);

                //calculate side tabs too
                // left_tab_width = sideTab;
                if (attackAngle > 0 && extendTabs) {
                    top_tab_left_attack_adjustment =
                        ((left_tab_width + top_pitch_extend_adjustment) * Math.sin(attackAngle)) / Math.cos(pitchAngle);
                    top_tab_left_width = (left_tab_width + top_pitch_extend_adjustment) * Math.cos(attackAngle);
                    // top_tab_right_width = right_tab_width * Math.cos(attackAngle);
                }

                // right_tab_width = sideTab;
                if (attackAngle < 0 && extendTabs) {
                    top_tab_right_attack_adjustment =
                        ((right_tab_width + top_pitch_extend_adjustment) * Math.sin(attackAngle)) /
                        Math.cos(pitchAngle);
                    top_tab_right_width = (right_tab_width + top_pitch_extend_adjustment) * Math.cos(attackAngle);
                }
                shape = [
                    new Vector2(top_tab_left_width, -bottom_tab_length / 2 + top_tab_left_attack_adjustment),
                    new Vector2(0, -bottom_tab_length / 2),
                    new Vector2(0, bottom_tab_length / 2),
                    new Vector2(top_tab_right_width, bottom_tab_length / 2 + top_tab_right_attack_adjustment),
                ];

                const top_tab_tri = new PolygonMeshBuilder("polytri", shape, scene, earcut);
                const top_tab = top_tab_tri.build(false, gauge); //updatable or not, depth
                top_tab.parent = bracket;
                top_tab.position.y =
                    -(height / 2) +
                    gauge +
                    (Math.max(Math.abs(back_left_compound_adjustment), Math.abs(back_right_compound_adjustment)) / 2) *
                        (pitchAngle > 0 ? 1 : -1);

                top_tab.position.y = top_tab.position.y + height / Math.cos(pitchAngle) + 1;

                if (!flat) top_tab.rotation.z = -pitchAngle * Math.cos(Math.abs(attackAngle)); // * (attackAngle && pitchAngle ? Math.sin(Math.abs(attackAngle)) * 1.05 : 1);

                top_tab.rotation.x = attackAngle * Math.cos(attackAngle) * Math.tan(pitchAngle) * (1.65 - adj_down); // * (attackAngle && pitchAngle ? -Math.sin(attackAngle) * 1.05 : 1);

                if (flat) top_tab.rotation.z = degreeToRadians(90);

                top_tab.material = bracketMaterial;
            }

            // LEFT TAB
            let hole_side_position_gap = 15;
            let top_pitch_adjustment = Math.tan(pitchAngle) * (left_tab_width + top_pitch_extend_adjustment);
            let bottom_pitch_adjustment = Math.tan(pitchAngle) * (left_tab_width + bottom_pitch_extend_adjustment);
            let left_tab_hole_pitch_adjustment =
                1.0 * Math.tan(pitchAngle) * (left_tab_width - hole_side_position_gap - tek_holeSize / 2);

            let left_tab_height = (height + 2) / Math.cos(pitchAngle);

            shape = [
                new Vector2(left_tab_height / 2, 0),
                new Vector2(-left_tab_height / 2, 0),
                new Vector2(
                    -left_tab_height / 2 - bottom_pitch_adjustment,
                    left_tab_width + bottom_pitch_extend_adjustment
                ),
                new Vector2(left_tab_height / 2 - top_pitch_adjustment, left_tab_width + top_pitch_extend_adjustment),
            ];
            const left_tab_tri = new PolygonMeshBuilder("polytri", shape, scene, earcut);

            holePosX = left_tab_height / 2 - bracket_hole_inset - left_tab_hole_pitch_adjustment;
            holePosY = left_tab_width - hole_side_position_gap;
            mountHole = new Path2(holePosX - tek_holeSize / 2, holePosY);
            mountHole.addArcTo(holePosX, holePosY + tek_holeSize / 2, holePosX + tek_holeSize / 2, holePosY, 20);
            mountHole.addArcTo(holePosX, holePosY - tek_holeSize / 2, holePosX - tek_holeSize / 2, holePosY, 20);
            left_tab_tri.addHole(mountHole.getPoints());

            holePosX = -left_tab_height / 2 + bracket_hole_inset - left_tab_hole_pitch_adjustment;
            holePosY = left_tab_width - hole_side_position_gap;
            mountHole = new Path2(holePosX - tek_holeSize / 2, holePosY);
            mountHole.addArcTo(holePosX, holePosY + tek_holeSize / 2, holePosX + tek_holeSize / 2, holePosY, 20);
            mountHole.addArcTo(holePosX, holePosY - tek_holeSize / 2, holePosX - tek_holeSize / 2, holePosY, 20);
            left_tab_tri.addHole(mountHole.getPoints());

            const left_tab = left_tab_tri.build(false, gauge); //updatable or not, depth
            left_tab.parent = bracket;
            left_tab.position.z = -back_width / 2;
            left_tab.position.y = (left_tab_height - height) / 2 + back_left_compound_adjustment;
            if (flat) left_tab.rotation.y = Math.PI;
            else left_tab.rotation.y = Math.PI / 2 - attackAngle;
            left_tab.rotation.z = Math.PI / 2;

            left_tab.material = bracketMaterial;

            // RIGHT TAB
            top_pitch_adjustment = Math.tan(pitchAngle) * (right_tab_width + top_pitch_extend_adjustment);
            bottom_pitch_adjustment = Math.tan(pitchAngle) * (right_tab_width + bottom_pitch_extend_adjustment);
            let right_tab_hole_pitch_adjustment =
                1.0 * Math.tan(pitchAngle) * (right_tab_width - hole_side_position_gap - tek_holeSize / 2);

            let right_tab_height = (height + 2) / Math.cos(pitchAngle);

            shape = [
                new Vector2(right_tab_height / 2, 0),
                new Vector2(-right_tab_height / 2, 0),
                new Vector2(
                    -right_tab_height / 2 - bottom_pitch_adjustment,
                    right_tab_width + bottom_pitch_extend_adjustment
                ),
                new Vector2(right_tab_height / 2 - top_pitch_adjustment, right_tab_width + top_pitch_extend_adjustment),
            ];
            const right_tab_tri = new PolygonMeshBuilder("polytri", shape, scene, earcut);

            holePosX = right_tab_height / 2 - bracket_hole_inset - right_tab_hole_pitch_adjustment;
            holePosY = right_tab_width - hole_side_position_gap;
            mountHole = new Path2(holePosX - tek_holeSize / 2, holePosY);
            mountHole.addArcTo(holePosX, holePosY + tek_holeSize / 2, holePosX + tek_holeSize / 2, holePosY, 20);
            mountHole.addArcTo(holePosX, holePosY - tek_holeSize / 2, holePosX - tek_holeSize / 2, holePosY, 20);
            right_tab_tri.addHole(mountHole.getPoints());

            holePosX = -right_tab_height / 2 + bracket_hole_inset - right_tab_hole_pitch_adjustment;
            holePosY = right_tab_width - hole_side_position_gap;
            mountHole = new Path2(holePosX - tek_holeSize / 2, holePosY);
            mountHole.addArcTo(holePosX, holePosY + tek_holeSize / 2, holePosX + tek_holeSize / 2, holePosY, 20);
            mountHole.addArcTo(holePosX, holePosY - tek_holeSize / 2, holePosX - tek_holeSize / 2, holePosY, 20);
            right_tab_tri.addHole(mountHole.getPoints());

            const right_tab = right_tab_tri.build(false, gauge); //updatable or not, depth
            right_tab.parent = bracket;
            right_tab.position.z = back_width / 2 + gauge;
            right_tab.position.y = (right_tab_height - height) / 2 + back_right_compound_adjustment;
            if (flat) right_tab.rotation.y = 0;
            else right_tab.rotation.y = Math.PI / 2 - attackAngle;
            right_tab.rotation.z = Math.PI / 2;
            right_tab.material = bracketMaterial;

            let beam_length = 300;
            const beam = CreateBox("beam", { height: height, width: width - 2, depth: beam_length }, scene);
            beam.parent = bracket;
            beam.setPivotPoint(new Vector3(0, -height / 2, -beam_length / 2));
            beam.rotation.y = Math.PI / 2 - attackAngle;
            beam.rotation.x = pitchAngle;
            beam.position.x = 0;
            beam.position.z = beam_length / 2;
            beam.position.y = gauge;

            if (attackAngle > 0)
                beam.position.y = beam.position.y + (width / 2) * Math.sin(pitchAngle) * Math.tan(attackAngle);
            if (attackAngle < 0)
                beam.position.y = beam.position.y - (width / 2) * Math.sin(pitchAngle) * Math.tan(attackAngle);

            beam.enableEdgesRendering(0.999);
            beam.edgesColor = new Color4(0.0, 0.0, 0.0, 0.3);
            beam.edgesWidth = 50;

            var beamMeterial = new StandardMaterial("redMat", scene);
            beamMeterial.diffuseColor = Color3.FromHexString(hex_colour);
            beamMeterial.ambientColor = Color3.FromHexString(hex_colour);
            beamMeterial.backFaceCulling = false;
            beamMeterial.alpha = 0.1;
            beam.material = beamMeterial;

            if (_scene_data?.scene?.meshes?.length && flat)
                for (var i = 0; i < _scene_data.scene.meshes.length; i++) {
                    let el = _scene_data.scene.meshes?.[i];
                    if (el) {
                        if (["skyBox", "beam", "fascia"].includes(el.name)) {
                        } else {
                            el.enableEdgesRendering(0.999);
                            el.edgesColor = new Color4(0.0, 0.0, 0.0, 1.0);
                            el.edgesWidth = 100;
                        }
                    }
                }

            return bracket;
        }
    };
    const addRotationalBracket = (
        width,
        height,
        topTab,
        sideTab,
        mountingHoles,
        rotationAngle,
        attackAngle,
        hex_colour = "#E9DCB8",
        gauge,
        backPlateHoles = true
    ) => {
        if (_scene_data.scene) {
            let scene = _scene_data.scene;

            let bracket_hole_inset = 25;
            let flat = flatten;
            if (height < 80) bracket_hole_inset = 20;
            let bracket_back_top_hole_inset = bracket_hole_inset;
            if (topTab > 30) bracket_back_top_hole_inset = 27;
            let shape;
            //Polygon shape in XoZ plane

            // FASCIA PLANES
            let fascia_length = 300;
            const fascia = CreateBox("fascia", { height: 185, width: 1, depth: fascia_length }, scene);

            fascia.setPivotPoint(new Vector3(0, -185 / 2, 0));

            fascia.enableEdgesRendering(0.999);
            fascia.edgesColor = new Color4(0.0, 0.0, 0.0, 0.3);
            fascia.edgesWidth = 50;

            var fasciaMeterial = new StandardMaterial("redMat", scene);
            fasciaMeterial.diffuseColor = Color3.FromHexString("#FFFFFF");
            fasciaMeterial.ambientColor = Color3.FromHexString("#FFFFFF");
            fasciaMeterial.backFaceCulling = false;
            fasciaMeterial.alpha = 0.6;
            fascia.material = fasciaMeterial;

            let top_adjust = 0;
            if (height + topTab > 185) top_adjust = 185 - (height + topTab);
            fascia.position.y = -height / 2 + 185 / 2 - top_adjust;
            fascia.position.x = -3;

            var bracketMaterial = new StandardMaterial("redMat", scene);
            bracketMaterial.diffuseColor = Color3.FromHexString(hex_colour);
            bracketMaterial.ambientColor = Color3.FromHexString(hex_colour);
            bracketMaterial.backFaceCulling = false;

            // BACK PLANE
            // BACK PLATE

            let back_width = width / (1 - (1 - Math.cos(attackAngle)) * Math.cos(Math.abs(rotationAngle)) * 0.88);
            let back_height =
                height /
                (1 -
                    (1 - Math.cos(attackAngle)) *
                        Math.cos(Math.abs(rotationAngle)) *
                        Math.pow(Math.tan(Math.abs(rotationAngle)), 1.6));
            let back_left_compound_adjustment =
                attackAngle > 0 ? back_width * Math.tan(pitchAngle) * Math.sin(attackAngle) : 0;
            let back_right_compound_adjustment =
                attackAngle < 0 ? back_width * Math.tan(pitchAngle) * -Math.sin(attackAngle) : 0;

            let back_left_tab_height_adj =
                topTab < 0
                    ? height / Math.cos(pitchAngle) - height + back_left_compound_adjustment
                    : -height * Math.cos(pitchAngle) + height;
            let back_right_tab_height_adj =
                topTab < 0
                    ? height / Math.cos(pitchAngle) - height + back_right_compound_adjustment
                    : -height * Math.cos(pitchAngle) + height;

            let topFoldedTab = false;
            if (topTab < 0) {
                topTab = 0;
                topFoldedTab = true;
            }

            shape = [new Vector2(0, 0), new Vector2(0, 1), new Vector2(1, 1), new Vector2(0, 0)];
            const bracket_tri = new PolygonMeshBuilder("bracket", shape, scene, earcut);
            const bracket = bracket_tri.build(false, gauge); //updatable or not, depth

            let shear_adj = beamShear;
            shear_adj += 540 * Math.pow(1 - Math.cos(attackAngle), 1.6) * (1 - Math.cos(rotationAngle));
            let shear_adj_x = beamShearX;

            shape = [
                new Vector2(back_height / 2 - shear_adj / 2, -back_width / 2 - shear_adj_x / 2),
                new Vector2(-back_height / 2 - shear_adj, -back_width / 2 + shear_adj_x / 2),
                new Vector2(-back_height / 2 + shear_adj / 2, back_width / 2 - shear_adj_x / 2),
                new Vector2(back_height / 2 + shear_adj, back_width / 2 + shear_adj_x / 2),
            ];

            const back_plate_tri = new PolygonMeshBuilder("polytri", shape, scene, earcut);

            const back_plate = back_plate_tri.build(false, gauge); //updatable or not, depth
            back_plate.parent = bracket;
            back_plate.rotation.z = Math.PI / 2;

            let extra_rotation = beamRotationAngleExtra;
            extra_rotation += 130 * Math.pow(1 - Math.cos(attackAngle), 1.1) * (1 - Math.cos(rotationAngle));
            back_plate.rotation.x = rotationAngle + degreeToRadians(extra_rotation);
            back_plate.material = bracketMaterial;

            var bracketMaterial = new StandardMaterial("redMat", scene);
            bracketMaterial.diffuseColor = Color3.FromHexString(hex_colour);
            bracketMaterial.ambientColor = Color3.FromHexString(hex_colour);
            bracketMaterial.backFaceCulling = false;

            let beam_length = 300;
            const beam = CreateBox("beam", { height: height, width: width - 2, depth: beam_length + 300 }, scene);
            beam.setPivotPoint(new Vector3(0, 0, -beam_length / 2));
            beam.position.z = beam_length / 2;

            beam.rotation.y = Math.PI / 2 - attackAngle;
            beam.rotation.z = rotationAngle;

            beam.enableEdgesRendering(0.999);
            beam.edgesColor = new Color4(0.0, 0.0, 0.0, 0.3);
            beam.edgesWidth = 50;

            var beamMeterial = new StandardMaterial("redMat", scene);
            beamMeterial.diffuseColor = Color3.FromHexString(hex_colour);
            beamMeterial.ambientColor = Color3.FromHexString(hex_colour);
            beamMeterial.backFaceCulling = false;
            beamMeterial.alpha = 0.1;
            beam.material = beamMeterial;

            if (_scene_data?.scene?.meshes?.length && flat)
                for (var i = 0; i < _scene_data.scene.meshes.length; i++) {
                    let el = _scene_data.scene.meshes?.[i];
                    if (el) {
                        if (["skyBox", "beam", "fascia"].includes(el.name)) {
                        } else {
                            el.enableEdgesRendering(0.999);
                            el.edgesColor = new Color4(0.0, 0.0, 0.0, 1.0);
                            el.edgesWidth = 100;
                        }
                    }
                }

            return bracket;
        }
    };
    const onRender = (scene) => {
        if (polygon !== undefined) {
            var deltaTimeInMillis = scene.getEngine().getDeltaTime();

            const rpm = 5;
            polygon.rotation.y += (rpm / 60) * Math.PI * 2 * (deltaTimeInMillis / 1000);
        }
    };
    const onSceneReady = (scene, job) => {
        const canvas = scene.getEngine().getRenderingCanvas();

        scene.getEngine().setHardwareScalingLevel(1 / window.devicePixelRatio);
        _scene_data.scene = scene;
        _scene_data.canvas = canvas;

        // Add a camera to the scene and attach it to the canvas

        let camera_alpha = Math.PI;
        let camera_beta = Math.PI * 0.15;
        if (bracketType === "Hip Lower") {
            camera_alpha = -2.3856485631641204;
            camera_beta = 0.41625910008796885;
        } else if (defaults.AttackAngle === 90 && defaults.PitchAngle !== 0) {
            camera_alpha = -Math.PI / 2;
            camera_beta = Math.PI / 2;
        } else if (defaults.AttackAngle !== 90 && defaults.PitchAngle !== 0) {
            camera_alpha = -2.3662831271376237;
            camera_beta = 1.1317543028628763;
        }

        var camera = new ArcRotateCamera("Camera", camera_alpha, camera_beta, 600, new Vector3(125, 40, 0), scene);
        camera.attachControl(canvas, true);
        camera.minZ = 5;
        camera.maxZ = 10000;
        camera.upperRadiusLimit = 5000;
        camera.lowerRadiusLimit = 10;
        camera.wheelPrecision = 0.75;
        camera.panningSensibility = 9;
        _scene_data.camera = camera;

        window._scene_data = {};
        window._scene_data.scene = scene;
        window._scene_data.camera = camera;

        // return;

        var skybox = CreateBox("skyBox", { size: 18000.0 }, scene);
        var skyboxMaterial = new StandardMaterial("skyBox", scene);
        skyboxMaterial.backFaceCulling = false;
        skyboxMaterial.reflectionTexture = new CubeTexture(BaseURL("textures/test/TropicalSunnyDay"), scene);
        skyboxMaterial.reflectionTexture.coordinatesMode = SKYBOX_MODE;
        skyboxMaterial.diffuseColor = new Color3(0, 0, 0);
        skyboxMaterial.specularColor = new Color3(0, 0, 0);
        skybox.material = skyboxMaterial;

        var skybox2 = CreateBox("skyBox2", { size: 10000.0 }, scene);
        var skyboxMaterial2 = new StandardMaterial("skyBox2", scene);
        skyboxMaterial2.backFaceCulling = false;
        skyboxMaterial2.reflectionTexture = new CubeTexture(BaseURL("textures/test/TropicalSunnyDay"), scene);
        skyboxMaterial2.reflectionTexture.coordinatesMode = SKYBOX_MODE;
        skyboxMaterial2.diffuseColor = new Color3(0, 0, 0);
        skyboxMaterial2.specularColor = new Color3(0, 0, 0);
        skyboxMaterial2.alpha = 0.5;
        skybox2.rotation.y += Math.PI;
        skybox2.material = skyboxMaterial2;

        var run_sky_backwards = false;
        scene.registerBeforeRender(function () {
            if (run_sky_backwards) skybox2.rotation.y -= 0.0005;
            else skybox2.rotation.y += 0.0005;
        });

        skybox.isVisible = false;
        _scene_data.skybox = skybox;
        skybox2.isVisible = false;
        _scene_data.skybox2 = skybox2;

        var background = new Layer("background", BaseURL("models/textures/bg.jpg"), scene, true);
        background.isBackground = true;
        background.isVisible = true;
        _scene_data.background = background;

        scene.activeCamera = camera;

        // Add lights to the scene
        var light1 = new HemisphericLight("light1", new Vector3(1, 0.8, 0), scene);
        light1.intensity = 0.8;
        window._scene_data.hemisphere_light = light1;

        var direction_light = new DirectionalLight("dirLight", new Vector3(1, -1, -0.5), scene);
        direction_light.intensity = 0.3;
        direction_light.position = new Vector3(1200, -1000, 1500);
        window._scene_data.direction_light = direction_light;

        scene.registerBeforeRender(function () {});

        //Light direction is directly down from a position one unit up, slow decay
        var torch = new SpotLight("spotLight", new Vector3(-1, 1, -1), new Vector3(0, -1, 0), Math.PI / 2, 30, scene);
        torch.diffuse = new Color3(0.5, 0.5, 0.5);
        torch.position = camera.position;
        torch.position.x = camera.position.x - 200;
        torch.position.y = camera.position.y - 600;
        torch.specular = new Color3(0.5, 0.5, 0.5);
        torch.intensity = 0.3;

        scene.registerBeforeRender(function () {
            torch.position = camera.position;
            torch.setDirectionToTarget(new Vector3(0, 0, 0));
        });

        // Shadows
        shadowGenerator = new ShadowGenerator(2048, direction_light);
        shadowGenerator.bias = 0.002;
        shadowGenerator.normalBias = 0.05;
        direction_light.shadowMaxZ = 6000;
        direction_light.shadowMinZ = 0;
        shadowGenerator.useBlurExponentialShadowMap = true;
        shadowGenerator.blurBoxOffset = 16;
        shadowGenerator.blurScale = 4;
        shadowGenerator.useKernelBlur = true;
        shadowGenerator.blurKernel = 256;
        shadowGenerator.useContactHardeningShadow = true;
        shadowGenerator.contactHardeningLightSizeUVRatio = 0.02;
        shadowGenerator.setDarkness(0.1);
        shadowGenerator.transparencyShadow = true;
        shadowGenerator.enableSoftTransparentShadow = true;
        shadowGenerator.usePoissonSampling = true;
        shadowGenerator.usePercentageCloserFiltering = true;

        _scene_data.shadowGenerator = shadowGenerator;
        window._scene_data.shadowGenerator = shadowGenerator;

        if (!initialise) setInitialise(true);
    };

    return (
        <BSL_Dialog
            title={viewMode ? "Bracket Viewer" : "Bracket Designer"}
            open={show}
            onClose={() => {
                setShow(false);
                onClose();
            }}
            button1Text={mode === "View" || mode === "Designer" ? "Close" : "Update"}
            button1Click={() => {
                // saveData();
                if (mode === "View" || mode === "Designer") {
                    setShow(false);
                    onClose();
                } else {
                    setShow(false);

                    let new_bracket_data = GenerateBracketDataForOpal(
                        bracketData,
                        bracketType,
                        attackAngle,
                        pitchAngle,
                        joinAngle,
                        rotationAngle,
                        topTab,
                        sideTab,
                        beamType,
                        gauge,
                        colour,
                        1,
                        null,
                        extendTabs
                    );

                    props.onUpdate(item, new_bracket_data);
                }
            }}
            button2Text={mode === "View" || mode === "Designer" ? "" : "Cancel"}
            button2Click={() => {
                setShow(false);
                onClose();
            }}
            style={{ maxWidth: "initial" }}
            fullWidth={true}
            maxWidth={"md"}
        >
            <BSL_Common.SectionRow>
                <BSL_Common.SectionColumn
                    style={{
                        paddingRight: "20px",
                    }}
                >
                    <BSL_Common.SectionRow>
                        {!viewMode && !limitedEdit && (
                            <BSL_Select
                                label="Bracket Type"
                                style={{ flex: "none" }}
                                tight={true}
                                value={bracketType}
                                items={
                                    viewMode
                                        ? [bracketType]
                                        : [
                                              { Key: "Custom", Display: "Custom Backet" },
                                              { Key: "Rotated", Display: "Custom Rotated Compound Bracket" },
                                              { Key: "Connector", Display: "Beam Connector End to End" },
                                              { Key: "Hip Upper", Display: "Hip Rafter Upper Hanging Corner" },
                                              { Key: "Hip Lower", Display: "Hip Rafter Lower Internal Corner" },
                                          ]
                                }
                                onChange={(e) => {
                                    setBracketType(e.target.value);
                                    if (e.target.value === "Custom") {
                                        setAttackAngle(90);
                                        setPitchAngle(0);
                                    }
                                    if (e.target.value === "Connector") {
                                        setJoinAngle(90);
                                        setExtendTabs(true);
                                    }
                                    if (e.target.value === "Hip Upper") {
                                        setJoinAngle(90);
                                        setAttackAngle(0);
                                        setPitchAngle(-5);
                                    }
                                    if (e.target.value === "Hip Lower") {
                                        setJoinAngle(90);
                                        setAttackAngle(0);
                                        setPitchAngle(5);
                                    }
                                }}
                            />
                        )}
                    </BSL_Common.SectionRow>

                    {bracketType === "Connector" && (
                        <BSL_Slider
                            label={"Join Angle"}
                            tight
                            value={joinAngle}
                            disabled={viewMode || limitedEdit}
                            min={45}
                            max={180}
                            suffix={"°"}
                            marks={[
                                {
                                    value: 45,
                                    label: "45°",
                                },
                                { value: 60 },
                                { value: 75 },
                                {
                                    value: 90,
                                    label: "90°",
                                },
                                { value: 105 },
                                { value: 120 },
                                {
                                    value: 135,
                                    label: "135°",
                                },
                                { value: 150 },
                                { value: 165 },
                                {
                                    value: 180,
                                    label: "180°",
                                },
                            ]}
                            step={1}
                            onChange={(e, val) => {
                                setJoinAngle(val);
                            }}
                            onChangeStep={(e, val) => {
                                setJoinAngle(val);
                            }}
                        />
                    )}

                    {(bracketType === "Hip Upper" || bracketType === "Hip Lower") && (
                        <>
                            <BSL_Slider
                                label={"Back Corner Angle"}
                                tight
                                value={joinAngle}
                                disabled={viewMode || limitedEdit}
                                min={45}
                                max={135}
                                suffix={"°"}
                                marks={[
                                    {
                                        value: 45,
                                        label: "45°",
                                    },
                                    { value: 60 },
                                    { value: 75 },
                                    {
                                        value: 90,
                                        label: (
                                            <div
                                                style={{
                                                    marginLeft: "8px",
                                                    fontWeight: "800",
                                                }}
                                            >
                                                90°
                                            </div>
                                        ),
                                    },
                                    { value: 105 },
                                    { value: 120 },
                                    {
                                        value: 135,
                                        label: "135°",
                                    },
                                ]}
                                step={1}
                                onChange={(e, val) => {
                                    setJoinAngle(val);
                                }}
                                onChangeStep={(e, val) => {
                                    setJoinAngle(val);
                                }}
                            />

                            <BSL_Slider
                                label={"Beam Attack Angle"}
                                tight
                                value={attackAngle}
                                disabled={viewMode || limitedEdit}
                                min={-30}
                                max={30}
                                marks={[
                                    {
                                        value: -30,
                                        label: (
                                            <div
                                                style={{
                                                    lineHeight: 1,
                                                    paddingTop: "5px",
                                                }}
                                            >
                                                {Math.round(joinAngle / 2) - 30}°
                                                <br />
                                                (Left)
                                            </div>
                                        ),
                                    },
                                    { value: -25 },
                                    { value: -20 },
                                    {
                                        value: -15,
                                        label: Math.round(joinAngle / 2) - 15 + "°",
                                    },
                                    { value: -10 },
                                    {
                                        value: -5,
                                        label: Math.round(joinAngle / 2) - 5 + "°",
                                    },
                                    {
                                        value: 0,
                                        label: (
                                            <div
                                                style={{
                                                    fontWeight: "800",
                                                }}
                                            >
                                                {Math.round(joinAngle / 2)}°
                                            </div>
                                        ),
                                    },
                                    {
                                        value: 5,
                                        label: Math.round(joinAngle / 2) - 5 + "°",
                                    },
                                    { value: 10 },
                                    {
                                        value: 15,
                                        label: Math.round(joinAngle / 2) - 15 + "°",
                                    },
                                    { value: 20 },
                                    { value: 25 },
                                    {
                                        value: 30,
                                        label: (
                                            <div
                                                style={{
                                                    lineHeight: 1,
                                                    paddingTop: "5px",
                                                }}
                                            >
                                                {Math.round(joinAngle / 2) - 30}°
                                                <br />
                                                (Right)
                                            </div>
                                        ),
                                    },
                                ]}
                                step={1}
                                onChange={(e, val) => {
                                    setAttackAngle(val);
                                }}
                                onChangeStep={(e, val) => {
                                    setAttackAngle(val);
                                }}
                                valueLabelFormat={(val) => {
                                    let hand = "";
                                    let display_angle = Math.round(joinAngle / 2) + Number(val);
                                    if (Number(val) < 0) hand = "LH";
                                    else if (Number(val) > 0) {
                                        hand = "RH";
                                        display_angle = Math.round(joinAngle / 2) - Number(val);
                                    }

                                    return display_angle + "° " + hand;
                                }}
                            />
                            <BSL_Slider
                                label={"Beam Pitch Angle"}
                                tight
                                value={pitchAngle}
                                disabled={viewMode || limitedEdit}
                                min={-30}
                                max={30}
                                suffix={"°"}
                                marks={[
                                    {
                                        value: -30,
                                        label: "-30° (Down)",
                                    },
                                    { value: -25 },
                                    { value: -20 },
                                    {
                                        value: -15,
                                        label: "-15°",
                                    },
                                    { value: -10 },
                                    {
                                        value: -5,
                                        label: "-5°",
                                    },
                                    {
                                        value: 0,
                                        label: (
                                            <div
                                                style={{
                                                    marginLeft: "4px",
                                                    fontWeight: "800",
                                                }}
                                            >
                                                0°
                                            </div>
                                        ),
                                    },
                                    {
                                        value: 5,
                                        label: "5°",
                                    },
                                    { value: 10 },
                                    {
                                        value: 15,
                                        label: "15°",
                                    },
                                    { value: 20 },
                                    { value: 25 },
                                    {
                                        value: 30,
                                        label: "30° (Up)",
                                    },
                                ]}
                                step={1}
                                onChange={(e, val) => {
                                    setPitchAngle(val);
                                }}
                                onChangeStep={(e, val) => {
                                    setPitchAngle(val);
                                }}
                            />
                        </>
                    )}

                    {bracketType === "Custom" && (
                        <>
                            {page_setup?.LeftRight && (
                                <BSL_Slider
                                    label={"Attack Angle"}
                                    tight
                                    value={attackAngle}
                                    disabled={viewMode || limitedEdit}
                                    min={30}
                                    max={150}
                                    suffix={"°"}
                                    marks={[
                                        {
                                            value: 30,
                                            label: (
                                                <div
                                                    style={{
                                                        lineHeight: 1,
                                                        paddingTop: "5px",
                                                    }}
                                                >
                                                    30°
                                                    <br />
                                                    (Left)
                                                </div>
                                            ),
                                        },
                                        { value: 35 },
                                        { value: 40 },
                                        {
                                            value: 45,
                                            label: "45°",
                                        },
                                        { value: 50 },
                                        { value: 55 },
                                        {
                                            value: 60,
                                            label: "60°",
                                        },
                                        { value: 65 },
                                        { value: 70 },
                                        {
                                            value: 75,
                                            label: "75°",
                                        },
                                        { value: 80 },
                                        {
                                            value: 85,
                                        },
                                        {
                                            value: 90,
                                            label: (
                                                <div
                                                    style={{
                                                        marginLeft: "8px",
                                                        fontWeight: "800",
                                                    }}
                                                >
                                                    90°
                                                </div>
                                            ),
                                        },
                                        {
                                            value: 95,
                                        },
                                        { value: 100 },
                                        {
                                            value: 105,
                                            label: "75°",
                                        },
                                        { value: 110 },
                                        { value: 115 },
                                        {
                                            value: 120,
                                            label: "60°",
                                        },
                                        { value: 125 },
                                        { value: 130 },
                                        {
                                            value: 135,
                                            label: "45°",
                                        },
                                        { value: 140 },
                                        { value: 145 },
                                        {
                                            value: 150,
                                            label: (
                                                <div
                                                    style={{
                                                        lineHeight: 1,
                                                        paddingTop: "5px",
                                                    }}
                                                >
                                                    30°
                                                    <br />
                                                    (Right)
                                                </div>
                                            ),
                                        },
                                    ]}
                                    step={1}
                                    onChange={(e, val) => {
                                        setAttackAngle(val);
                                    }}
                                    onChangeStep={(e, val) => {
                                        setAttackAngle(val);
                                    }}
                                    customTextField={(val) => {
                                        let hand = "";
                                        let display_angle = val;
                                        if (Number(val) < 90) hand = "LH";
                                        else if (Number(val) > 90) {
                                            hand = "RH";
                                            display_angle = 180 - Number(val);
                                        }

                                        return (
                                            <div className={classes.label}>
                                                {display_angle}° {hand}
                                            </div>
                                        );
                                    }}
                                    valueLabelFormat={(val) => {
                                        let hand = "";
                                        let display_angle = val;
                                        if (Number(val) < 90) hand = "LH";
                                        else if (Number(val) > 90) {
                                            hand = "RH";
                                            display_angle = 180 - Number(val);
                                        }

                                        return display_angle + "° " + hand;
                                    }}
                                />
                            )}

                            {page_setup?.UpDown && (
                                <BSL_Slider
                                    label={"Pitch Angle"}
                                    tight
                                    value={pitchAngle}
                                    disabled={viewMode || limitedEdit}
                                    min={-30}
                                    max={30}
                                    suffix={"°"}
                                    marks={[
                                        {
                                            value: -30,
                                            label: (
                                                <div
                                                    style={{
                                                        lineHeight: 1,
                                                        paddingTop: "5px",
                                                    }}
                                                >
                                                    30°
                                                    <br />
                                                    (Up)
                                                </div>
                                            ),
                                        },
                                        { value: -25, label: "25°" },
                                        { value: -20, label: "20°" },
                                        {
                                            value: -15,
                                            label: "15°",
                                        },
                                        { value: -10, label: "10°" },
                                        {
                                            value: -5,
                                            label: "5°",
                                        },
                                        {
                                            value: 0,
                                            label: (
                                                <div
                                                    style={{
                                                        marginLeft: "4px",
                                                        fontWeight: "800",
                                                    }}
                                                >
                                                    0°
                                                </div>
                                            ),
                                        },
                                        {
                                            value: 5,
                                            label: "5°",
                                        },
                                        { value: 10, label: "10°" },
                                        {
                                            value: 15,
                                            label: "15°",
                                        },
                                        { value: 20, label: "20°" },
                                        { value: 25, label: "25°" },
                                        {
                                            value: 30,
                                            label: (
                                                <div
                                                    style={{
                                                        lineHeight: 1,
                                                        paddingTop: "5px",
                                                    }}
                                                >
                                                    30°
                                                    <br />
                                                    (Down)
                                                </div>
                                            ),
                                        },
                                    ]}
                                    step={1}
                                    onChange={(e, val) => {
                                        setPitchAngle(val);
                                    }}
                                    onChangeStep={(e, val) => {
                                        setPitchAngle(val);
                                    }}
                                    valueLabelFormat={(val) => {
                                        let hand = "";
                                        let display_angle = Math.abs(Number(val));
                                        if (Number(val) < 0) hand = "UP";
                                        else if (Number(val) > 0) {
                                            hand = "DOWN";
                                        }

                                        return display_angle + "° " + hand;
                                    }}
                                    helperText={() => {
                                        let hand = "";
                                        let display_angle = perpendicular_pitch_compount_angle;
                                        if (Number(pitchAngle) < 0) hand = "UP";
                                        else if (Number(pitchAngle) > 0) {
                                            hand = "DOWN";
                                        }
                                        return "Perpendicular Beam Pitch:" + display_angle.toFixed(1) + "° " + hand;
                                    }}
                                />
                            )}
                            <BSL_Common.SectionRow>
                                <BSL_Slider
                                    label={"Top Tab"}
                                    tight
                                    value={topTab}
                                    disabled={viewMode}
                                    min={-54}
                                    max={108}
                                    suffix={"mm"}
                                    marks={[
                                        {
                                            value: -54,
                                            label: "Folded Tab",
                                        },
                                        {
                                            value: 0,
                                            label: "0",
                                        },
                                        {
                                            value: 54,
                                            label: (
                                                <div
                                                    style={{
                                                        lineHeight: 1,
                                                        paddingTop: "5px",
                                                    }}
                                                >
                                                    54mm
                                                    <br />
                                                    (Standard)
                                                </div>
                                            ),
                                        },
                                        {
                                            value: 108,
                                            label: (
                                                <div
                                                    style={{
                                                        lineHeight: 1,
                                                        paddingTop: "5px",
                                                    }}
                                                >
                                                    108mm
                                                    <br />
                                                    (Long)
                                                </div>
                                            ),
                                        },
                                    ]}
                                    step={1}
                                    onChange={(e, val) => {
                                        if (val < -27) setTopTab(-54);
                                        else if (val < 5) setTopTab(0);
                                        else if (val > 51 && val < 57) setTopTab(54);
                                        else setTopTab(val);
                                    }}
                                    onChangeStep={(e, val) => {
                                        if (val < -27) setTopTab(-54);
                                        else if (val < 5) setTopTab(0);
                                        else setTopTab(val);
                                    }}
                                />
                                <BSL_Slider
                                    label={"Minimum Side Tab"}
                                    tight
                                    value={sideTab}
                                    disabled={viewMode}
                                    min={30}
                                    max={60}
                                    suffix={"mm"}
                                    marks={[
                                        {
                                            value: 30,
                                            label: "30 (Standard)",
                                        },
                                        {
                                            value: 60,
                                            label: "60 (Max)",
                                        },
                                    ]}
                                    step={1}
                                    onChange={(e, val) => {
                                        setSideTab(val);
                                    }}
                                    onChangeStep={(e, val) => {
                                        setSideTab(val);
                                    }}
                                    helperText={
                                        Math.abs(attackAngle) < 40 || Math.abs(attackAngle) > 140 ? (
                                            <span style={{ color: "#a30000" }}>
                                                <WarningIcon
                                                    style={{ color: "orange", width: "12px", height: "12px" }}
                                                />
                                                for Attack angle {">"} 40 degrees, tab size of 60mm recommended
                                            </span>
                                        ) : (
                                            ""
                                        )
                                    }
                                />
                            </BSL_Common.SectionRow>
                        </>
                    )}
                    {bracketType === "Rotated" && (
                        <>
                            {page_setup?.LeftRight && (
                                <BSL_Slider
                                    label={"Attack Angle"}
                                    tight
                                    value={attackAngle}
                                    disabled={viewMode || limitedEdit}
                                    min={20}
                                    max={160}
                                    suffix={"°"}
                                    marks={[
                                        {
                                            value: 30,
                                            label: (
                                                <div
                                                    style={{
                                                        lineHeight: 1,
                                                        paddingTop: "5px",
                                                    }}
                                                >
                                                    30°
                                                    <br />
                                                    (Left)
                                                </div>
                                            ),
                                        },
                                        { value: 35 },
                                        { value: 40 },
                                        {
                                            value: 45,
                                            label: "45°",
                                        },
                                        { value: 50 },
                                        { value: 55 },
                                        {
                                            value: 60,
                                            label: "60°",
                                        },
                                        { value: 65 },
                                        { value: 70 },
                                        {
                                            value: 75,
                                            label: "75°",
                                        },
                                        { value: 80 },
                                        {
                                            value: 85,
                                        },
                                        {
                                            value: 90,
                                            label: (
                                                <div
                                                    style={{
                                                        marginLeft: "8px",
                                                        fontWeight: "800",
                                                    }}
                                                >
                                                    90°
                                                </div>
                                            ),
                                        },
                                        {
                                            value: 95,
                                        },
                                        { value: 100 },
                                        {
                                            value: 105,
                                            label: "75°",
                                        },
                                        { value: 110 },
                                        { value: 115 },
                                        {
                                            value: 120,
                                            label: "60°",
                                        },
                                        { value: 125 },
                                        { value: 130 },
                                        {
                                            value: 135,
                                            label: "45°",
                                        },
                                        { value: 140 },
                                        { value: 145 },
                                        {
                                            value: 150,
                                            label: (
                                                <div
                                                    style={{
                                                        lineHeight: 1,
                                                        paddingTop: "5px",
                                                    }}
                                                >
                                                    30°
                                                    <br />
                                                    (Right)
                                                </div>
                                            ),
                                        },
                                    ]}
                                    step={1}
                                    onChange={(e, val) => {
                                        setAttackAngle(val);
                                    }}
                                    onChangeStep={(e, val) => {
                                        setAttackAngle(val);
                                    }}
                                    customTextField={(val) => {
                                        let hand = "";
                                        let display_angle = val;
                                        if (Number(val) < 90) hand = "LH";
                                        else if (Number(val) > 90) {
                                            hand = "RH";
                                            display_angle = 180 - Number(val);
                                        }

                                        return (
                                            <div className={classes.label}>
                                                {display_angle}° {hand}
                                            </div>
                                        );
                                    }}
                                    valueLabelFormat={(val) => {
                                        let hand = "";
                                        let display_angle = val;
                                        if (Number(val) < 90) hand = "LH";
                                        else if (Number(val) > 90) {
                                            hand = "RH";
                                            display_angle = 180 - Number(val);
                                        }

                                        return display_angle + "° " + hand;
                                    }}
                                />
                            )}

                            <BSL_Slider
                                label={"Rotated Angle"}
                                tight
                                value={rotationAngle}
                                disabled={viewMode || limitedEdit}
                                min={-45}
                                max={45}
                                suffix={"°"}
                                marks={[
                                    {
                                        value: -45,
                                        label: "-45° (Left)",
                                    },
                                    { value: -40 },
                                    { value: -35 },
                                    {
                                        value: -30,
                                        label: "-30°",
                                    },
                                    { value: -25 },
                                    { value: -20 },
                                    {
                                        value: -15,
                                        label: "-15°",
                                    },
                                    { value: -10, label: "-10°" },
                                    {
                                        value: -5,
                                        label: "-5°",
                                    },
                                    {
                                        value: 0,
                                        label: "0°",
                                    },
                                    {
                                        value: 5,
                                        label: "5°",
                                    },
                                    { value: 10, label: "10" },
                                    {
                                        value: 15,
                                        label: "15°",
                                    },
                                    { value: 20 },
                                    { value: 25 },
                                    {
                                        value: 30,
                                        label: "30°",
                                    },
                                    { value: 35 },
                                    { value: 40 },
                                    {
                                        value: 45,
                                        label: "45° (Right)",
                                    },
                                ]}
                                step={1}
                                onChange={(e, val) => {
                                    setRotationAngle(val);
                                }}
                                onChangeStep={(e, val) => {
                                    setRotationAngle(val);
                                }}
                            />
                        </>
                    )}
                    <BSL_Common.ElementHeader tight>Material</BSL_Common.ElementHeader>
                    <BSL_Common.SectionRow>
                        <BSL_Select
                            label="Beam Section"
                            tight={true}
                            value={beamType}
                            items={
                                viewMode || limitedEdit
                                    ? [beamType]
                                    : window.LYSAGHT
                                    ? [
                                          { Key: "FL10011", Display: "FL10011" },
                                          { Key: "FL15015", Display: "FL15015" },
                                          { Key: "FL20020", Display: "FL20020" },
                                      ]
                                    : [
                                          { Key: "656510", Display: "Cen Box 65" },
                                          { Key: "1086510", Display: "Cen Box 108" },
                                          { Key: "1586510", Display: "Cen Box 158" },
                                          { Key: "2086510", Display: "Cen Box 208" },
                                      ]
                            }
                            onChange={(e) => setBeamType(e.target.value)}
                        />
                        <BSL_Select
                            label="Steel Gauge"
                            tight={true}
                            // disabled={viewMode}
                            value={viewMode || limitedEdit ? gauge + "mm GALV P/C" : gauge}
                            items={
                                viewMode || limitedEdit
                                    ? [gauge + "mm GALV P/C"]
                                    : [
                                          { Key: 1.2, Display: "1.2mm GALV P/C" },
                                          { Key: 1.6, Display: "1.6mm GALV P/C" },
                                          { Key: 2.0, Display: "2.0mm GALV P/C" },
                                      ]
                            }
                            onChange={(e) => setGauge(e.target.value)}
                        />
                    </BSL_Common.SectionRow>
                    <BSL_Common.SectionRow>
                        <BSL_ColourSelector
                            label="Colour"
                            tight
                            colourList={viewMode || limitedEdit ? [colour] : colours}
                            value={colour}
                            onChange={(val) => setColour(val)}
                            style={
                                {
                                    // marginTop: "-12px",
                                }
                            }
                        />
                    </BSL_Common.SectionRow>
                </BSL_Common.SectionColumn>
                <BSL_Common.SectionColumn>
                    <Babylon3DGeneric
                        antialias
                        engineOptions={{ preserveDrawingBuffer: true, stencil: true }}
                        onSceneReady={onSceneReady}
                        onRender={onRender}
                        style={{
                            zIndex: 0,
                            display: "block",
                            left: "0px",
                            backgroundColor: "rgba(255, 255, 255, 255)",
                            width: "100%",
                            height: "600px",
                            color: "#000",
                            // transition: "visibility 0.3s, opacity 0.3s linear",
                        }}
                        id="my-canvas-bracket"
                    />
                </BSL_Common.SectionColumn>
            </BSL_Common.SectionRow>
            {(window.debug || window.test || mode === "Designer") && (
                <div
                    style={{
                        background: "#f5deb36b",
                        borderRadius: "7px",
                        padding: "2px",
                    }}
                >
                    {mode === "Designer" && (
                        <BSL_Common.SectionRow>
                            <BSL_TextInput
                                label={"Job Number"}
                                style={{ width: "200px", color: "white" }}
                                value={job?.id}
                                extratight={true}
                            />
                            <BSL_TextInput
                                label={"Bracket Name"}
                                style={{ width: "200px", color: "white" }}
                                value={drawingDetails?.["Bracket Name"]}
                                extratight={true}
                                // preValidate={"name"}
                                onEnter={(e) =>
                                    setDrawingDetails({ ...drawingDetails, "Bracket Name": e.target.value })
                                }
                                onChange={(val) => setDrawingDetails({ ...drawingDetails, "Bracket Name": val })}
                            />
                            <BSL_TextInput
                                label={"Drawing Name"}
                                style={{ width: "200px", color: "white" }}
                                value={drawingDetails?.["Drawing Name"]}
                                extratight={true}
                                // preValidate={"name"}
                                onEnter={(e) =>
                                    setDrawingDetails({ ...drawingDetails, "Drawing Name": e.target.value })
                                }
                                onChange={(val) => setDrawingDetails({ ...drawingDetails, "Drawing Name": val })}
                            />
                            <BSL_TextInput
                                label={"Use"}
                                style={{ width: "200px", color: "white" }}
                                value={drawingDetails?.["Use"]}
                                extratight={true}
                                onEnter={(e) => setDrawingDetails({ ...drawingDetails, Use: e.target.value })}
                                onChange={(val) => setDrawingDetails({ ...drawingDetails, Use: val })}
                            />
                            <BSL_TextInput
                                label={"Qty"}
                                style={{ width: "60px", color: "white", flex: "none" }}
                                value={drawingDetails?.["Num"]}
                                extratight={true}
                                preValidate="number"
                                min={0}
                                ma={99}
                                onEnter={(e) => setDrawingDetails({ ...drawingDetails, Num: toNumber(e.target.value) })}
                                onChange={(val) => setDrawingDetails({ ...drawingDetails, Num: toNumber(val) })}
                            />
                        </BSL_Common.SectionRow>
                    )}
                    <BSL_Common.SectionRow>
                        {user.Role >= 90 && (
                            <>
                                <SegmentedButtonGroup
                                    label="Flatten"
                                    style={{ width: "100px", color: "white", flex: "none" }}
                                    extratight={true}
                                    value={flatten}
                                    allowToggle
                                    items={[
                                        { Key: true, Display: "On" },
                                        { Key: false, Display: "Off" },
                                    ]}
                                    onChange={(e, val) => {
                                        setFlatten(!flatten);
                                    }}
                                />
                                <SegmentedButtonGroup
                                    label="Tab Extend"
                                    extratight={true}
                                    value={extendTabs}
                                    allowToggle
                                    items={[
                                        { Key: true, Display: "Extend on Angle" },
                                        { Key: false, Display: "Not Extended" },
                                    ]}
                                    onChange={(e, val) => {
                                        setExtendTabs(val);
                                    }}
                                />
                            </>
                        )}
                        {user.Role >= 99 && (
                            <SegmentedButtonGroup
                                label="Mode"
                                extratight={true}
                                value={viewMode}
                                allowToggle
                                items={
                                    mode === "Designer"
                                        ? [
                                              { Key: true, Display: "View" },
                                              { Key: "FullEdit", Display: "Edit" },
                                          ]
                                        : [
                                              { Key: true, Display: "View" },
                                              { Key: false, Display: "Edit" },
                                              { Key: "FullEdit", Display: "Full Edit" },
                                          ]
                                }
                                onChange={(e, val) => {
                                    if (val === "FullEdit") {
                                        setViewMode(false);
                                        setLimitedEdit(false);
                                    } else {
                                        setViewMode(val);
                                    }
                                }}
                            />
                        )}
                        <form method="post" target="_blank" action={API_BASE().replace("/api/", "/") + `Bracket/Draw/`}>
                            <input
                                id="new_window_bracket_data"
                                name="data"
                                type="hidden"
                                value={JSON.stringify({
                                    user_id: user_id,
                                    job_id: job?.id,
                                    fielders: window.FIELDERS,
                                    brand: window.LYSAGHT ? "Lysaght" : "Fielders",
                                    bracket_data: GenerateBracketDataForOpal(
                                        bracketData,
                                        bracketType,
                                        attackAngle,
                                        pitchAngle,
                                        joinAngle,
                                        rotationAngle,
                                        topTab,
                                        sideTab,
                                        beamType,
                                        gauge,
                                        colour,
                                        1,
                                        null,
                                        extendTabs
                                    ),
                                    drawing_data: drawingDetails,
                                })}
                            />

                            <BSL_Button
                                type="submit"
                                extratight={true}
                                secondary
                                style={{
                                    margin: "18px 0px 5px",
                                    padding: "5px 5px",
                                    width: "140px",
                                    height: "32px",
                                }}
                                onClick={() => {}}
                            >
                                PREVIEW LAYOUT
                            </BSL_Button>
                        </form>
                    </BSL_Common.SectionRow>
                    {drawingSVG && (
                        <BSL_Common.SectionRow>
                            <BSL_Dialog
                                id="template_preview"
                                title={"Layout Template Preview"}
                                style={{ maxWidth: "90% !important" }}
                                contentStyle={{ maxWidth: "90% !important" }}
                                maxWidth={"90%"}
                                open={drawingSVG}
                                onClose={() => {
                                    setDrawingSVG(null);
                                }}
                                button1Text="Close"
                                button1Click={() => {
                                    setDrawingSVG(null);
                                }}
                            >
                                {drawingSVG === "loading" ? (
                                    <CircularProgress color="inherit" style={{ position: "absolute" }} />
                                ) : (
                                    <img src={drawingSVG} />
                                )}
                            </BSL_Dialog>
                        </BSL_Common.SectionRow>
                    )}
                </div>
            )}
        </BSL_Dialog>
    );
}
